/**
 * DIE ZENTRALE PRODUKTAKTE.
 *
 * Eine durchgehende Arbeitsflaeche fuer EINEN Artikel, in der Lesereihenfolge
 * der Masterpreisliste:
 *
 *   1 Artikelidentitaet   2 Lichttechnik   3 Stromtechnik   4 Konstruktion
 *   5 Verpackung          6 Zoll           7 Einkauf        8 Zuschlaege
 *   9 Importkalkulation  10 Verkaufspreis
 *
 * WARUM EINE FLAECHE UND NICHT ZEHN REITER
 *
 * Die Arbeitsansicht (`ArticleDetailPanel`) zeigt bewusst jeweils EINEN
 * Bereich: dort wird an einer Sache gearbeitet, und alles andere waere
 * Ablenkung. Die Akte ist der andere Fall -- hier wird ein Artikel als Ganzes
 * gepflegt, oft von oben nach unten, und der Zusammenhang zwischen
 * Einkaufspreis, Zuschlaegen und Listenpreis ist genau das, was man sehen
 * will. Zehn Reiter wuerden diese Kette zerschneiden.
 *
 * Die Seite darf deshalb scrollen. Die Sprungleiste bleibt stehen und zeigt,
 * wo man ist.
 *
 * KEINE SCHATTENLOGIK
 *
 * Diese Datei rechnet nichts. Kein Listenpreis, keine Marge, keine
 * Umrechnung -- auch keine „nur zur Anzeige". Was hier steht, kommt aus der
 * API; was hier geaendert wird, geht an die API und wird von dort neu
 * gelesen. Die Serverlogik ist die Wahrheit (Auftrag Abschnitt 21).
 *
 * KEIN FELD ERFINDET EINEN WERT
 *
 * Ein leeres Feld heisst „derzeit nicht bekannt" und nichts sonst. Es wird
 * nicht mit 0 gefuellt, nicht aus der Produktfamilie geerbt und nicht aus
 * einem aehnlichen Artikel uebernommen.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type {
  AirFreightDerivation, ArticleCalculation, ArticleCalculationResponse, ArticleDetail, ArticleDossier, ArticleLogisticsResponse,
  CalculationScenariosResponse, ModelReleaseScope,
  ArticleVariantSpaceResponse, CalculationParametersResponse, Capability,
  DossierSectionCode, MetaResponse, VariantDimension, VariantOptionValue,
} from "@homebase/shared";
import { DOSSIER_SECTIONS, DOSSIER_SECTION_CAPABILITY, PRICE_CURRENCIES, erreichtMindestmarge,
         margeNachProvision, preisstufen } from "@homebase/shared";
import { IconChevron, IconFlugzeug, IconSchiff, IconZug } from "./Icons.tsx";
import { api, ApiError } from "../lib/api.ts";
import { heuteLokal } from "../lib/datum.ts";
import { TRANSPORTLAUFZEIT } from "../lib/transportwege.ts";
import { displayValue } from "./ProductProperties.tsx";
import { useI18n, type I18n } from "../i18n/index.tsx";
import { errorText } from "../i18n/errors.ts";
import { ErrorState, LoadingState, Unknown } from "./States.tsx";
import { ProductProperties } from "./ProductProperties.tsx";
import { ArticlePackagingSection, ArticlePurchasingSection, TariffCode } from "./ArticleLogistics.tsx";
import { categoryLabel } from "../i18n/terminology.ts";
import "./ProductDossier.css";


/*
 * DER VARIANTENRAUM -- getrennt von der konkreten SKU.
 *
 * Der Auftrag verlangt diese Trennung ausdruecklich, und sie ist nicht
 * kosmetisch. „CRI: >90 Ra" an der SKU heisst: DIESER Artikel hat 90 Ra.
 * „CRI Standard >80 Ra, optional >90 Ra" heisst: es gibt ihn in beidem. Wer
 * das nebeneinander in eine Merkmalstabelle schreibt, erzeugt einen Artikel,
 * der zwei Farbwiedergaben zugleich haette.
 *
 * Deshalb ein eigener Abschnitt mit eigener Ueberschrift, eigenem Vorspann --
 * und der ausdruecklichen Aussage, dass hier keine SKU entsteht.
 */
function VariantSpace({ articleId, i18n }: {
  articleId: number; i18n: I18n;
}): React.JSX.Element {
  const [raum, setRaum] = useState<ArticleVariantSpaceResponse | null>(null);
  const [fehler, setFehler] = useState<unknown>(null);
  useEffect(() => {
    let weg = false;
    api.variantSpace(articleId)
      .then((r) => { if (!weg) setRaum(r); })
      .catch((e) => { if (!weg) setFehler(e); });
    return () => { weg = true; };
  }, [articleId]);

  if (fehler !== null) return <ErrorState message={errorText(i18n, fehler)} />;
  if (raum === null) return <LoadingState />;
  if (raum.dimensions.length === 0) {
    return <p className="akte__hint">{i18n.t("varianten.empty")}</p>;
  }
  return (
    <>
      <p className="akte__hint">{i18n.t("varianten.lead")}</p>
      <dl className="varianten">
        {raum.dimensions.map((d) => <VariantRow key={d.id} d={d} i18n={i18n} />)}
      </dl>
      <p className="akte__hint varianten__note">{i18n.t("varianten.noSku")}</p>
    </>
  );
}

function VariantRow({ d, i18n }: { d: VariantDimension; i18n: I18n }): React.JSX.Element {
  /*
   * Der Standard steht vorn und benannt, die uebrigen dahinter. Gibt es
   * keinen, steht das AUSDRUECKLICH da -- „gleichrangige Ausfuehrungen ohne
   * benannten Standard". Die zwoelf Winkel der AREA-PRO-FL-Serie sind genau
   * dieser Fall; eine leere Standardzeile liesse sie wie eine Luecke aussehen.
   */
  const standard = d.options.filter((o) => o.isStandard);
  const weitere = d.options.filter((o) => !o.isStandard);
  const text = (o: VariantOptionValue): string =>
    o.raw ?? displayValue(d.definition, {
      id: o.id, definition: d.definition.code, valueIndex: 0, num: o.num, operator: null,
      min: o.min, max: o.max, bool: o.bool, code: o.code, text: o.text,
      origin: "import", method: "uebernommen", quality: "pruefung_erforderlich",
      sourceSegment: null, updatedAt: "",
    }, i18n, false);
  return (
    <>
      <dt>{i18n.tCode("prop", d.definition.code, d.definition.code)}</dt>
      <dd>
        <span className="varianten__standard">
          {standard.length > 0
            ? <>{i18n.t("varianten.standard")}: <strong>{standard.map(text).join(" · ")}</strong></>
            : <em>{i18n.t("varianten.noStandard")}</em>}
        </span>
        {weitere.length > 0 && (
          <span className="varianten__optional">
            {i18n.t("varianten.optional")}: {weitere.map(text).join(" · ")}
          </span>
        )}
        {/*
          * Der Wortlaut der Quelle bleibt lesbar -- er traegt das „>", das
          * die strukturierte Zahl nicht fuehrt. Er steht aber AUFGEKLAPPT
          * und nicht mehr dauerhaft: „Standard 120°, optional 30° · 60° ·
          * 90°" ist die Aussage; die Rohzeile darunter ist ihr Beleg.
          */}
        {d.sourceSegment && (
          <details className="varianten__prov">
            <summary>{i18n.t("varianten.source")}</summary>
            <span className="varianten__source">{d.sourceSegment}</span>
          </details>
        )}
      </dd>
    </>
  );
}

/** Die Abschnitte, die dieser Nutzer ueberhaupt sieht. */
function visibleDossierSections(
  capabilities: readonly Capability[],
): readonly DossierSectionCode[] {
  return DOSSIER_SECTIONS.filter((s) => {
    const noetig = DOSSIER_SECTION_CAPABILITY[s];
    return noetig === undefined || capabilities.includes(noetig);
  });
}


/** Warum eine Ausfuehrung fuer eine Modellfreigabe nicht pruefbar ist -- woertlich fuer checki18n. */
const BLOCKER_KEY = {
  no_calculation: "mp.blocker.no_calculation",
  calculation_incomplete: "mp.blocker.calculation_incomplete",
  basis_incomplete: "mp.blocker.basis_incomplete",
} as const;

export function ProductDossier({
  articleId, meta, capabilities, mayWriteArticle, mayWriteProps, mayConfirmProps,
  mayWriteLogistics, mayConfirmLogistics, onBack, onTrashed,
}: {
  articleId: number;
  meta: MetaResponse;
  capabilities: readonly Capability[];
  mayWriteArticle: boolean;
  mayWriteProps: boolean;
  mayConfirmProps: boolean;
  mayWriteLogistics: boolean;
  mayConfirmLogistics: boolean;
  onBack: () => void;
  /**
   * Das Produkt wurde weggeraeumt -- die Akte schliesst sich, die Liste laedt
   * neu. `undefined`, wenn der Nutzer nicht loeschen darf: dann erscheint die
   * Handlung gar nicht.
   */
  onTrashed?: () => void;
}): React.JSX.Element {
  const i18n = useI18n();
  const t = i18n.t;
  const [a, setA] = useState<ArticleDetail | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [current, setCurrent] = useState<DossierSectionCode>("identitaet");
  /*
   * Die Aktenantwort traegt Beziehungen, Leuchten je LS und offene
   * Aenderungsvorschlaege -- alles in EINER Abfrage. Sie war bisher gebaut
   * und wurde von keiner Ansicht gelesen.
   */
  const [akte, setAkte] = useState<ArticleDossier | null>(null);
  /*
   * Die Aktenantwort neu holen -- nach einer Vorschlagsentscheidung.
   *
   * Sie traegt die offenen Vorschlaege UND die Zahlen des Produktstatus im
   * Kopf. Beides muss sich sofort bewegen, sonst zeigt die Oberflaeche einen
   * Konflikt an, den es nicht mehr gibt.
   */
  const akteNeu = (): void => {
    api.articleDossier(articleId).then(setAkte).catch(() => undefined);
  };
  const flow = useRef<HTMLDivElement | null>(null);

  const kopf = useRef<HTMLElement | null>(null);

  /*
   * DIE HOEHE DER SPRUNGLEISTE WIRD GEMESSEN, NICHT GERATEN.
   *
   * `scroll-margin-top` stand auf festen 7rem. Das passt genau so lange, wie
   * die Leiste einzeilig bleibt -- bei 1194 px bricht sie auf zwei Zeilen um,
   * und dann verschwindet die Abschnittsueberschrift hinter ihr: man landet im
   * Abschnitt und sieht den Rest des vorherigen.
   *
   * Die tatsaechliche Hoehe steht als CSS-Variable am Dokument und wird bei
   * jeder Groessenaenderung nachgefuehrt -- auch beim Umbruch der Leiste.
   */
  useEffect(() => {
    const el = kopf.current;
    if (!el) return;
    const messen = (): void => {
      /*
       * DIE HAUPTNAVIGATION ZAEHLT MIT (UI CONSISTENCY 001, §5).
       *
       * Solange die Akte ihren eigenen Bildlauf hatte, begann dieser
       * UNTER der Hauptnavigation -- die Kopfhoehe der Akte allein
       * genuegte als Sprungabstand. Seit das FENSTER scrollt, klebt
       * die Sprungleiste bei `top: var(--header-h)`, und ein Ziel
       * ohne diesen Zuschlag landet HINTER beiden Leisten.
       *
       * Gemessen statt gesetzt: `getBoundingClientRect().top` des
       * Kopfes im klebenden Zustand IST der Abstand nach oben. Damit
       * stimmt die Zahl auch dann, wenn die Hauptnavigation einmal
       * eine andere Hoehe bekommt.
       */
      const r = el.getBoundingClientRect();
      const oben = Math.max(0, Math.round(r.top));
      /*
       * ZWEI ZAHLEN AUS EINER MESSUNG -- und sie unterscheiden sich
       * um genau den Atemzug.
       *
       *   --akte-lesekante  die UNTERKANTE der klebenden Leiste.
       *                     Darueber ist nichts zu lesen. Danach
       *                     richtet sich, welcher Abschnitt als
       *                     „der oben stehende" gilt.
       *   --akte-kopfhoehe  dieselbe Kante plus 12px Luft. Danach
       *                     richtet sich das SPRUNGZIEL: eine
       *                     Ueberschrift, die bundig an der Leiste
       *                     klebt, sieht aus wie ein Fehler.
       *
       * VORHER GAB ES NUR DIE ZWEITE, und der Beobachter hatte seine
       * eigene, festverdrahtete Schaetzung (80px). Gemessen: die
       * beiden Zahlen zeigten auf verschiedene Abschnitte, und die
       * Markierung meldete einen anderen als den sichtbaren. Jetzt
       * kommen beide aus derselben Messung, und die 12px sind der
       * einzige Unterschied -- ausgesprochen, nicht geerbt.
       */
      const kante = Math.ceil(r.height) + oben;
      document.documentElement.style.setProperty("--akte-lesekante", `${kante}px`);
      document.documentElement.style.setProperty(
        "--akte-kopfhoehe", `${kante + 12}px`);
    };
    messen();
    const beobachter = new ResizeObserver(messen);
    beobachter.observe(el);
    return () => beobachter.disconnect();
    /*
     * ABHAENGIG VON BEIDEN LADEVORGAENGEN -- und das ist eine
     * Berichtigung, kein Feinschliff.
     *
     * Hier stand nur `[akte]`. Der Kopf entsteht aber, sobald `a`
     * geladen ist, und `a` und `akte` kommen aus ZWEI Anfragen.
     * Traf `akte` zuerst ein, lief dieser Effekt, fand
     * `kopf.current` noch leer, stieg aus -- und lief nie wieder,
     * weil sich `akte` danach nicht mehr aenderte.
     *
     * FOLGE: `--akte-kopfhoehe` blieb LEER, und jedes
     * `scroll-margin-top` fiel auf seinen Ersatzwert von 7rem
     * zurueck. Das fiel nicht auf, solange die Akte ihren eigenen
     * Bildlauf hatte: 112px genuegten dort. Seit §5 klebt die
     * Sprungleiste 56px tiefer, und das Sprungziel landete hinter
     * ihr -- gemessen: Ziel bei 112px, Leistenunterkante bei 182px.
     *
     * Mit `a` in der Liste laeuft der Effekt, sobald der Kopf
     * wirklich dasteht.
     */
  }, [a, akte]);

  const abschnitte = useMemo(() => visibleDossierSections(capabilities), [capabilities]);
  const darfKalkulation = capabilities.includes("purchasing.read");

  useEffect(() => {
    let cancelled = false;
    setA(null); setError(null); setCurrent("identitaet"); setAkte(null);
    api.articleDossier(articleId).then(setAkte).catch(() => setAkte(null));
    api.article(articleId)
      .then((r) => { if (!cancelled) setA(r); })
      .catch((e) => { if (!cancelled) setError(e); });
    return () => { cancelled = true; };
  }, [articleId]);

  /*
   * Welcher Abschnitt gerade gelesen wird.
   *
   * Ohne diese Rueckmeldung waere die Sprungleiste eine Einbahnstrasse: man
   * kaeme irgendwohin, wuesste aber nicht mehr, wo man ist. Beobachtet wird
   * das obere Drittel -- ein Abschnitt gilt als „aktuell", sobald seine
   * Ueberschrift dort steht, nicht erst wenn er die ganze Seite fuellt.
   */
  useEffect(() => {
    if (!a) return;
    const beobachter = new IntersectionObserver((eintraege) => {
      const sichtbar = eintraege
        .filter((e) => e.isIntersecting)
        .sort((x, y) => x.boundingClientRect.top - y.boundingClientRect.top)[0];
      if (sichtbar) setCurrent(sichtbar.target.id.replace("akte-", "") as DossierSectionCode);
      /*
       * DAS LESEFENSTER BEGINNT DORT, WO DER KOPF AUFHOERT -- und
       * zwar an DERSELBEN Zahl, die auch der Sprung benutzt.
       *
       * Hier standen 80 Pixel fest. Sie waren eine zweite, unabhaengige
       * Schaetzung derselben Groesse: der Sprung nahm
       * `--akte-kopfhoehe`, der Beobachter nahm 80. Solange beide
       * zufaellig nahe beieinander lagen, fiel es nicht auf; seit §5
       * die Hauptnavigation dazukommt, zeigten sie auf verschiedene
       * Abschnitte -- die Markierung meldete einen anderen als den
       * sichtbaren (AV13).
       */
    }, {
      rootMargin: `-${
        getComputedStyle(document.documentElement)
          .getPropertyValue("--akte-lesekante").trim() || "112px"
      } 0px -60% 0px`,
      threshold: 0,
    });
    for (const s of abschnitte) {
      const el = document.getElementById(`akte-${s}`);
      if (el) beobachter.observe(el);
    }
    return () => beobachter.disconnect();
  }, [a, abschnitte]);

  const springe = useCallback((s: DossierSectionCode) => {
    /*
     * Die Markierung folgt dem KLICK, nicht erst dem Scrollen.
     *
     * Der Beobachter unten meldet den sichtbaren Abschnitt zuverlaessig,
     * solange man rollt -- beim Sprung an einen Anker meldet er je nach
     * Reihenfolge der Ereignisse gar nichts, weil zwischen Ausgangs- und
     * Zielabschnitt kein Zwischenzustand entsteht. Dann stuende die
     * Markierung noch auf dem Abschnitt, den man gerade verlassen hat.
     */
    setCurrent(s);
    /*
     * OHNE Laufbewegung. Ein weiches Scrollen ueber sieben Abschnitte dauert
     * laenger, als man auf ein Ziel zu warten bereit ist, und wer die
     * Sprungleiste benutzt, will ANKOMMEN und nicht unterwegs sein. Es
     * respektiert ausserdem `prefers-reduced-motion` ohne Sonderfall.
     */
    document.getElementById(`akte-${s}`)?.scrollIntoView({ block: "start" });
  }, []);

  if (error !== null && !a) {
    return <div className="akte"><ErrorState message={errorText(i18n, error)} /></div>;
  }
  if (!a) return <div className="akte"><LoadingState label={t("akte.loading")} /></div>;

  const familie = a.families.find((f) => f.isPrimary) ?? a.families[0] ?? null;

  return (
    <div className="akte">
      <header className="akte__head" ref={kopf}>
        <div className="akte__headline">
          <button className="btn" onClick={onBack}>{t("akte.back")}</button>
          <div>
            <h2 className="akte__title">{a.articleNumber}</h2>
            <p className="akte__sub">
              {categoryLabel(i18n, a.category.code, a.category.name)}
              {familie && <> · {familie.name}</>}
            </p>
          </div>
          {/*
            * OFFENE ARBEIT AM PRODUKT -- drei Zustaende, NICHT vermischt.
            *
            * Es sind drei verschiedene Aussagen, und sie verlangen drei
            * verschiedene Handlungen:
            *
            *   Aenderungsvorschlaege  jemand muss ENTSCHEIDEN
            *   Prüfung erforderlich   jemand muss BESTAETIGEN
            *   offene Prüfhinweise    jemand muss KLAEREN
            *
            * Beide Zahlen stammen aus vorhandenen Daten: `openProposals` aus
            * der Aktenantwort, der Pruefstand aus den Merkmalswerten selbst.
            *
            * WAS HIER BEWUSST FEHLT: eine Zahl fuer „Angaben fehlen". HOMEBASE
            * kennt keine Pflichtfelder je Artikel -- der deklarierte Kern
            * (`category_core_properties`) sagt, was eine Kategorie
            * VERSTAENDLICH macht, nicht was sie BRAUCHT. Eine Zahl daraus
            * waere eine erfundene Vollstaendigkeitsaussage. Was fehlt, steht
            * unveraendert benannt in der Aufmerksamkeitszone der
            * Produktansicht -- als Liste, nicht als Zaehler.
            */}
          <ProduktStatus akte={akte} a={a} i18n={i18n} />
        </div>
        {/*
          * Die Sprungleiste. Sie nummeriert nach der VOLLSTAENDIGEN
          * Gliederung: fehlt jemandem der Einkauf, bleibt die Konstruktion
          * trotzdem die 4. Sonst hiesse dieselbe Zahl bei zwei Nutzern etwas
          * anderes.
          */}
        <nav className="akte__jump" aria-label={t("akte.jumpAria")}>
          {abschnitte.map((s) => (
            <button key={s} type="button"
              className={s === current ? "akte__jumpitem akte__jumpitem--on" : "akte__jumpitem"}
              aria-current={s === current ? "true" : undefined}
              onClick={() => springe(s)}>
              <span className="akte__jumpnum">{DOSSIER_SECTIONS.indexOf(s) + 1}</span>
              {i18n.tCode("akteSection", s, s)}
            </button>
          ))}
        </nav>
      </header>

      <div className="akte__flow" ref={flow}>
        <Section code="identitaet" i18n={i18n}>
          <Identity a={a} meta={meta} mayWrite={mayWriteArticle}
            onChanged={setA} onError={setError} i18n={i18n} />
        </Section>

        <Section code="varianten" i18n={i18n}>
          <VariantSpace articleId={a.id} i18n={i18n} />
        </Section>

        {/*
          * Drei bis fuenf: dieselbe Merkmalspflege wie in der Arbeitsansicht,
          * nur nach der Gliederung der Preisliste aufgeteilt. Es ist DERSELBE
          * Datensatz -- wer hier einen Lichtstrom aendert, aendert ihn dort mit.
          */}
        <Section code="lichttechnik" i18n={i18n}>
          <ProductProperties subject={{ kind: "article", id: a.id }} meta={meta}
            mayWrite={mayWriteProps} mayConfirm={mayConfirmProps}
            groups={["lichttechnik"]} heading={null}
            proposals={akte?.openProposals ?? []}
            onProposalDecided={akteNeu} />
        </Section>

        <Section code="stromtechnik" i18n={i18n}>
          <ProductProperties subject={{ kind: "article", id: a.id }} meta={meta}
            mayWrite={mayWriteProps} mayConfirm={mayConfirmProps}
            groups={["elektrik"]} heading={null}
            proposals={akte?.openProposals ?? []}
            onProposalDecided={akteNeu} />
        </Section>

        {/* Gehaeuse, Abdeckung, Masse, Schutzart und Optik gehoeren in der
            Preisliste zusammen -- drei Merkmalsgruppen, ein Abschnitt. */}
        <Section code="konstruktion" i18n={i18n}>
          <ProductProperties subject={{ kind: "article", id: a.id }} meta={meta}
            mayWrite={mayWriteProps} mayConfirm={mayConfirmProps}
            groups={["mechanik", "schutz", "optik"]} heading={null}
            proposals={akte?.openProposals ?? []}
            onProposalDecided={akteNeu} />
        </Section>

        <Section code="verpackung" i18n={i18n}>
          <ArticlePackagingSection articleId={a.id} meta={meta}
            mayWrite={mayWriteLogistics} mayConfirm={mayConfirmLogistics}
            i18nLabel="" attention={null} showTariff={false} />
        </Section>

        <Section code="zoll" i18n={i18n}>
          <Customs articleId={a.id} mayWrite={mayWriteLogistics}
            mayWriteRate={capabilities.includes("price.parameter.write")} i18n={i18n} />
        </Section>

        {darfKalkulation && (
          <>
            <Section code="einkauf" i18n={i18n}>
              {/* Der Einkaufspreis steht und wird erfasst in der Lieferantenkarte --
                  ein eigener Block darunter war dieselbe Zahl ein zweites Mal. */}
              <ArticlePurchasingSection articleId={a.id} meta={meta}
                mayWrite={mayWriteLogistics} mayConfirm={mayConfirmLogistics}
                mayWritePrice={capabilities.includes("price.input.write")}
                i18nLabel="" attention={null} />
            </Section>

            {/*
              * Acht bis zehn haengen an EINER Abfrage: Zuschlaege, Rechenweg
              * und Ergebnis sind derselbe Kalkulationsstand, nur an drei
              * Stellen gelesen. Drei getrennte Ladevorgaenge koennten
              * unterschiedliche Staende zeigen -- auf einer Seite, ohne dass
              * es auffiele.
              */}
            <CalculationSections articleId={a.id}
              mayCalculate={capabilities.includes("price.input.write")}
              mayRelease={capabilities.includes("price.parameter.write")}
              mayUndercut={capabilities.includes("price.minimum_margin.override")}
              i18n={i18n} />
          </>
        )}
        {/*
          * DIE LOESCHAKTION STEHT GANZ UNTEN -- und zwar mit Absicht.
          *
          * Oben, neben „Zurueck", waere sie einen Fehlklick von jeder
          * Rueckkehr entfernt. Am Ende der Akte hat man das Produkt
          * durchgearbeitet, bevor man es weglegt; und sie ist dort nicht
          * versteckt, sondern schlicht dort, wo man ankommt.
          *
          * Sie loest NICHTS unmittelbar aus: der Knopf oeffnet eine
          * Rueckfrage, die die Artikelnummer nennt und einen Grund verlangt.
          */}
        {onTrashed !== undefined && (
          <TrashAction a={a} meta={meta} i18n={i18n} onDone={onTrashed} onError={setError} />
        )}
      </div>

      {error !== null && <p className="akte__error" role="alert">{errorText(i18n, error)}</p>}
    </div>
  );
}

/**
 * „In den Papierkorb verschieben".
 *
 * Die Rueckfrage nennt die Artikelnummer. „Sind Sie sicher?" beantwortet man
 * im Reflex -- „HB200-U4KD2D-85 in den Papierkorb verschieben?" liest man.
 */
export function TrashAction({ a, meta, i18n, onDone, onError }: {
  a: ArticleDetail;
  meta: MetaResponse;
  i18n: I18n;
  onDone: () => void;
  onError: (e: unknown) => void;
}): React.JSX.Element {
  const t = i18n.t;
  const [offen, setOffen] = useState(false);
  /* Der erste Grund des Wertevorrats ist die Vorgabe -- welcher das ist,
     entscheidet `sort_order` in der Datenbank, nicht diese Zeile. */
  const [grund, setGrund] = useState(meta.deletionReasons[0]?.code ?? "");
  const gewaehlt = meta.deletionReasons.find((r) => r.code === grund);
  const [notiz, setNotiz] = useState("");
  const [busy, setBusy] = useState(false);

  async function ausfuehren(): Promise<void> {
    setBusy(true);
    try {
      await api.trashArticle(a.id, { reasonCode: grund, note: notiz.trim() || null });
      setOffen(false); onDone();
    } catch (e) { onError(e); } finally { setBusy(false); }
  }

  if (!offen) {
    return (
      <div className="akte__danger">
        <button className="btn btn--danger" title={t("akte.trashTitle")}
          onClick={() => setOffen(true)}>{t("akte.trash")}</button>
      </div>
    );
  }
  return (
    <div className="akte__danger akte__danger--open" role="dialog"
         aria-label={t("akte.trashHeading", { number: a.articleNumber })}>
      <h4>{t("akte.trashHeading", { number: a.articleNumber })}</h4>
      <p>{t("akte.trashText")}</p>
      <label htmlFor="akte-trash-reason">{t("akte.trashReason")}</label>
      <select id="akte-trash-reason" className="list__select" value={grund}
        onChange={(e) => setGrund(e.target.value)}>
        {meta.deletionReasons.map((r) => (
          <option key={r.code} value={r.code}>
            {i18n.tCode("deletionReason", r.code, r.name)}
          </option>
        ))}
      </select>
      {/* Die Erlaeuterung ist bei „sonstiger Grund" Pflicht -- der Server
          besteht darauf, und die Oberflaeche sagt es vorher. */}
      <label htmlFor="akte-trash-note">{t("akte.trashNote")}</label>
      <input id="akte-trash-note" className="trash__input" value={notiz}
        placeholder={t("akte.trashNotePlaceholder")}
        onChange={(e) => setNotiz(e.target.value)} />
      <div className="akte__dangeractions">
        <button className="btn" onClick={() => setOffen(false)}>{t("akte.trashCancel")}</button>
        <button className="btn btn--danger"
          disabled={busy || (gewaehlt?.needsNote === true && notiz.trim() === "")}
          onClick={() => void ausfuehren()}>{t("akte.trashConfirm")}</button>
      </div>
    </div>
  );
}

/** Ein Abschnitt der Akte -- Nummer, Ueberschrift, Sprungziel. */
function Section({ code, i18n, children }: {
  code: DossierSectionCode;
  i18n: I18n;
  children: React.ReactNode;
}): React.JSX.Element {
  return (
    <section className="akte__section" id={`akte-${code}`}>
      <h3 className="akte__sectiontitle">
        <span className="akte__sectionnum">{DOSSIER_SECTIONS.indexOf(code) + 1}</span>
        {i18n.tCode("akteSection", code, code)}
      </h3>
      {children}
    </section>
  );
}

/* ══════════════════════════════════════════════════════════════════════════
 * 1 · ARTIKELIDENTITAET
 * ══════════════════════════════════════════════════════════════════════════ */

/**
 * Die Grunddaten.
 *
 * Die ARTIKELNUMMER steht hier, ist aber nicht aenderbar: sie ist die
 * Identitaet des Artikels. Eine Umbenennung haette Folgen fuer Beziehungen,
 * Listungen und Fremdsysteme und ist ein eigener fachlicher Vorgang -- kein
 * Feld, das man beim Durchgehen der Akte nebenbei ueberschreibt.
 *
 * Dasselbe gilt fuer Kategorie und Familie: die Kategorie folgt der Familie,
 * und ein Familienwechsel verschiebt unter anderem die Mindestmarge. Beides
 * gehoert nicht in die laufende Pflege.
 */
function Identity({ a, meta, mayWrite, onChanged, onError, i18n }: {
  a: ArticleDetail;
  meta: MetaResponse;
  mayWrite: boolean;
  onChanged: (a: ArticleDetail) => void;
  onError: (e: unknown) => void;
  i18n: I18n;
}): React.JSX.Element {
  const t = i18n.t;
  const [editing, setEditing] = useState(false);
  const [bezeichnung, setBezeichnung] = useState(a.shortDescription ?? "");
  const [status, setStatus] = useState<string>(a.lifecycleStatus ?? "");
  const [rolle, setRolle] = useState<string>(a.stockingType ?? "");
  const [busy, setBusy] = useState(false);

  const familie = a.families.find((f) => f.isPrimary) ?? a.families[0] ?? null;

  async function speichern(): Promise<void> {
    setBusy(true);
    try {
      onChanged(await api.updateArticle(a.id, {
        shortDescription: bezeichnung.trim() === "" ? null : bezeichnung.trim(),
        lifecycleStatus: status === "" ? null : status,
        stockingType: rolle === "" ? null : rolle,
      }));
      setEditing(false);
    } catch (e) { onError(e); } finally { setBusy(false); }
  }

  return (
    <>
      <dl className="akte__facts">
        <Fact label={t("akte.articleNumber")}><code>{a.articleNumber}</code></Fact>
        <Fact label={t("akte.category")}>
          {categoryLabel(i18n, a.category.code, a.category.name)}
        </Fact>
        <Fact label={t("akte.family")}>{familie ? familie.name : <Unknown />}</Fact>
        <Fact label={t("akte.model")}>
          {a.productModel ? `${a.productModel.name}` : <Unknown />}
        </Fact>
        {/*
          * DIE AUSPRAEGUNG STEHT BEIM MODELL -- und nur, wenn es eine gibt.
          *
          * „Produktmodell HB200 U" allein beantwortet die Frage nicht, die
          * jemand vor der Akte hat: WELCHE Ausfuehrung ist das hier? Die
          * Auspraegungen kommen aus `model_variants`, nicht aus den
          * Merkmalen -- sie sind die Aussage des Modells ueber diese SKU
          * und keine zweite Fassung ihrer Merkmalswerte.
          *
          * Ohne Variante steht hier nichts: eine leere Zeile „Ausfuehrung:
          * —" waere ein Versprechen auf eine Modellebene, die dieser
          * Artikel nicht hat.
          */}
        {a.variant && a.variant.values.length > 0 && (
          <Fact label={t("akte.variant")}>
            {a.variant.values.map((v) => {
              const wert = v.num
                ?? (v.min !== null && v.min === v.max ? v.min
                  : v.min !== null || v.max !== null ? `${v.min ?? ""}–${v.max ?? ""}` : null)
                ?? v.code ?? v.text ?? (v.bool === null ? "" : String(v.bool));
              return `${i18n.tCode("prop", v.definition, v.definition)}: ${wert}`;
            }).join(" · ")}
          </Fact>
        )}
        {/*
          * DIE BEZEICHNUNG IST KEIN KURZES FELD.
          *
          * „TP154 | L: 154 cm | 18 - 75 W | ≤ 11.250 lm | nw | IP65 |
          * 3-Phasen | CRI:90 | DALI 2 | 120°" sind 90 Zeichen. In einem
          * einzeiligen Eingabefeld sieht man davon ein Fuenftel und muss
          * waagerecht scrollen, um zu pruefen, was man aendert.
          *
          * Sie bekommt deshalb die volle Breite und mehrere Zeilen. Die
          * kurzen Identitaetsfelder daneben bleiben kompakt -- eine Nummer
          * braucht kein Textfeld.
          */}
        <Fact label={t("akte.sourceLabel")} breit>
          {editing
            ? <textarea className="input akte__textarea" value={bezeichnung} maxLength={500}
                rows={3}
                onChange={(e) => setBezeichnung(e.target.value)}
                aria-label={t("akte.sourceLabel")} />
            : (a.shortDescription ?? <Unknown />)}
        </Fact>
        <Fact label={t("akte.lifecycle")}>
          {editing
            ? <select className="input" value={status} onChange={(e) => setStatus(e.target.value)}
                      aria-label={t("akte.lifecycle")}>
                {/* Leer ist ein zulaessiger Zustand und heisst „nicht bestimmt" --
                    niemals „inaktiv". */}
                <option value="">{t("akte.lifecycleUnset")}</option>
                {meta.lifecycleStatuses.map((s) => (
                  <option key={s.code} value={s.code}>{i18n.tCode("lifecycle", s.code, s.name)}</option>
                ))}
              </select>
            : (a.lifecycleStatus
                ? i18n.tCode("lifecycle", a.lifecycleStatus, a.lifecycleStatus)
                : <Unknown />)}
        </Fact>
        {/*
          * DIE SORTIMENTSROLLE -- neben dem Sortimentsstatus, nicht in ihm.
          *
          * Zwei Entscheidungen, die man leicht zusammenwirft: „wird gefuehrt"
          * und „wie beschafft". Ein auslaufender Artikel kann Lagerware sein,
          * ein aktiver Projektware. Deshalb zwei Felder.
          *
          * Sie haengt an keiner Menge: ein Lagerartikel mit leerem Regal
          * bleibt Lagerware.
          */}
        <Fact label={t("akte.stockingType")}>
          {editing
            ? <select className="input" value={rolle} onChange={(e) => setRolle(e.target.value)}
                      aria-label={t("akte.stockingType")}>
                <option value="">{t("akte.stockingUnset")}</option>
                {meta.stockingTypes.map((s) => (
                  <option key={s.code} value={s.code}>{i18n.tCode("stocking", s.code, s.name)}</option>
                ))}
              </select>
            : (a.stockingType
                ? i18n.tCode("stocking", a.stockingType, a.stockingType)
                : <Unknown />)}
        </Fact>
        <Fact label={t("akte.roles")}>
          {a.roles.length === 0
            ? <Unknown />
            : a.roles.map((r) => i18n.tCode("articleRole", r.code, r.name)).join(", ")}
        </Fact>
      </dl>

      {mayWrite && (editing
        ? <div className="akte__actions">
            <button className="btn btn--primary" disabled={busy} onClick={speichern}>
              {t("common.save")}
            </button>
            <button className="btn" disabled={busy} onClick={() => {
              setBezeichnung(a.shortDescription ?? ""); setStatus(a.lifecycleStatus ?? "");
              setEditing(false);
            }}>{t("common.cancel")}</button>
          </div>
        : <button className="btn" onClick={() => setEditing(true)}>{t("akte.editBaseData")}</button>)}
    </>
  );
}

/* ══════════════════════════════════════════════════════════════════════════
 * 6 · ZOLL
 * ══════════════════════════════════════════════════════════════════════════ */

/**
 * Zolltarifnummer und Zollsatz.
 *
 * Zwei Angaben auf zwei verschiedenen Ebenen, und das ist der Grund, warum sie
 * hier zusammen stehen: die NUMMER gehoert zur SKU, der SATZ gehoert zur
 * Nummer. Wer den Satz aendert, aendert ihn fuer jeden Artikel derselben
 * Tarifnummer -- das muss man sehen koennen, bevor man es tut.
 */
function Customs({ articleId, mayWrite, mayWriteRate, i18n }: {
  articleId: number;
  mayWrite: boolean;
  mayWriteRate: boolean;
  i18n: I18n;
}): React.JSX.Element {
  const t = i18n.t;
  const [data, setData] = useState<ArticleLogisticsResponse | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [satz, setSatz] = useState("");
  const [ab, setAb] = useState("");
  const [offen, setOffen] = useState(false);
  const [busy, setBusy] = useState(false);
  /* Gegen den zweiten Klick eines Doppelklicks -- vor dem naechsten Render. */
  const laeuftZoll = useRef(false);

  const laden = useCallback(async () => {
    try { setData(await api.articleLogistics(articleId)); } catch (e) { setError(e); }
  }, [articleId]);
  useEffect(() => { setData(null); void laden(); }, [laden]);

  async function satzSpeichern(): Promise<void> {
    if (!data?.tariffCode || laeuftZoll.current || zollGrund !== null) return;
    laeuftZoll.current = true;
    setBusy(true);
    try {
      await api.setDutyRate({
        tariffCode: data.tariffCode, ratePercent: Number(satz.replace(",", ".")), validFrom: ab,
      });
      setSatz(""); setAb(""); setOffen(false);
      await laden();
    } catch (e) { setError(e); } finally { setBusy(false); laeuftZoll.current = false; }
  }
  /*
   * GUELTIG-AB IM FORMULARSTATE (Gueltig-ab-Formularstate 001).
   *
   * Das Datum begann leer. Safari zeigt ein leeres Datumsfeld mit dem
   * heutigen Datum an -- sichtbar gesetzt, im Formular leer, „Speichern"
   * grau ohne Grund. Jetzt setzt das Oeffnen den heutigen Kalendertag
   * wirklich, und ein gesperrter Knopf sagt, warum.
   */
  const zollWert = Number(satz.replace(",", "."));
  const zollGrund: string | null =
      satz.trim() === "" || !Number.isFinite(zollWert) || zollWert < 0 || zollWert > 100 ? t("form.why.rateMissing")
    : ab === "" ? t("form.why.dateMissing")
    : null;

  return (
    <>
      <TariffCode articleId={articleId} value={data?.tariffCode ?? null}
        inheritedFrom={data?.tariffInheritedFrom ?? null}
        mayWrite={mayWrite} onChanged={setData} onError={setError} />

      <dl className="akte__facts">
        <Fact label={t("akte.dutyRate")}>
          {data?.dutyRate
            ? <>{betrag(data.dutyRate.ratePercent, i18n.lang)} %{" "}
                <span className="akte__since">{t("akte.since", { date: data.dutyRate.validFrom })}</span></>
            : <Unknown />}
        </Fact>
      </dl>
      {/* Ohne Tarifnummer gibt es nichts, woran ein Satz haengen koennte. Das
          ist keine Fehlermeldung, sondern die Reihenfolge der Arbeit. */}
      {data && !data.tariffCode && <p className="akte__note">{t("akte.dutyNeedsTariff")}</p>}

      {mayWriteRate && data?.tariffCode && !offen && (
        <button className="btn akte__zollerfassen" onClick={() => {
          setSatz(""); setAb(heuteLokal()); setError(null); setOffen(true);
        }}>{t("akte.setDutyRate")}</button>
      )}
      {mayWriteRate && data?.tariffCode && offen && (
        <div className="akte__inline">
          <label>{t("akte.dutyRate")}
            <input className="input" inputMode="decimal" value={satz}
              onChange={(e) => setSatz(e.target.value)} />
          </label>
          <label>{t("akte.validFrom")}
            <input className="input" type="date" value={ab} onChange={(e) => setAb(e.target.value)} />
          </label>
          <button className="btn akte__zollspeichern" disabled={busy || zollGrund !== null} onClick={satzSpeichern}>
            {t("common.save")}
          </button>
          <button className="btn akte__zollabbrechen" onClick={() => { setSatz(""); setAb(""); setError(null); setOffen(false); }}>
            {t("common.cancel")}</button>
          {zollGrund !== null && <span className="akte__sperrgrund" role="status">{zollGrund}</span>}
        </div>
      )}
      {error !== null && <p className="akte__error" role="alert">{errorText(i18n, error)}</p>}
    </>
  );
}

/* ══════════════════════════════════════════════════════════════════════════
 * 7 · EINKAUFSPREIS
 * ══════════════════════════════════════════════════════════════════════════ */


/* ══════════════════════════════════════════════════════════════════════════
 * 8-10 · ZUSCHLAEGE, IMPORTKALKULATION, VERKAUFSPREIS
 * ══════════════════════════════════════════════════════════════════════════ */

/**
 * Die drei Abschnitte der Kalkulation -- aus EINEM Stand.
 *
 * Sie sind hier zusammengefasst, weil sie dieselbe Abfrage lesen: die
 * Zuschlaege sind die Eingaben des Standes, die Importkalkulation sein
 * Rechenweg, der Verkaufspreis sein Ergebnis. Getrennt geladen koennten sie
 * verschiedene Staende zeigen und dabei vollkommen widerspruchsfrei aussehen.
 *
 * Was hier NICHT passiert: rechnen. Kein Zwischenwert wird im Browser
 * gebildet. Wer eine neue Zahl will, laesst den Server einen neuen Stand
 * rechnen -- dann steht sie auch in der Historie.
 */
export function CalculationSections({ articleId, mayCalculate, mayRelease, mayUndercut, i18n,
                                     ansicht = "akte", onPreisGeaendert }: {
  articleId: number;
  /**
   * `akte`: die nummerierten Kapitel der Produktakte (Zuschlaege,
   * Importkalkulation, Verkaufspreis). `arbeitsflaeche`: der Reiter Einkauf --
   * Kette, Listenpreis mit Zustand, Details einen Klick weiter.
   */
  ansicht?: "akte" | "arbeitsflaeche";
  /** Nach „Neu rechnen" und nach einer Freigabe -- der Produktkopf liest den Preis neu. */
  onPreisGeaendert?: () => void;
  mayCalculate: boolean;
  mayRelease: boolean;
  /**
   * Darf dieser Nutzer die Zielmarge unterschreiten?
   *
   * Die Oberflaeche richtet sich danach -- entschieden wird im Server. Ohne
   * das Recht erscheint kein Weg zur Ausnahme; das ist keine Verschleierung,
   * sondern das Weglassen einer Schaltflaeche, die ohnehin abgewiesen wuerde.
   */
  mayUndercut: boolean;
  i18n: I18n;
}): React.JSX.Element {
  const t = i18n.t;
  const [calc, setCalc] = useState<ArticleCalculationResponse | null>(null);
  const [params, setParams] = useState<CalculationParametersResponse | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  const [freigabe, setFreigabe] = useState(false);
  const [preis, setPreis] = useState("");
  const [ab, setAb] = useState("");
  const [notiz, setNotiz] = useState("");
  /**
   * Die vom SERVER gemeldete Unterschreitung -- beide Zahlen, wie er sie
   * gerechnet hat, und ob sie ueberhaupt ausnahmefaehig ist.
   *
   * `gesperrt` heisst: dieser Nutzer darf nicht unterschreiten. Dann gibt es
   * keinen Weg weiter, auch keinen mit Begruendung.
   */
  const [unterschreitung, setUnterschreitung] =
    useState<{ resulting: string; minimum: string; gesperrt: boolean } | null>(null);
  const [grund, setGrund] = useState("");
  /*
   * DIE MODELLFREIGABE -- optional, ausdruecklich, fuer die VARIANTEN des
   * Produktmodells (nicht der Familie).
   *
   * `scope` nennt die Varianten und ihren Preisstand; `konflikte` sind die
   * abweichenden Freigaben, die der Server ohne Bestaetigung ablehnt. Die
   * Oberflaeche zeigt sie VOR dem Klick -- und verlangt die Bestaetigung,
   * bevor sie ueberhaupt fragt.
   */
  const [scope, setScope] = useState<ModelReleaseScope | null>(null);
  /*
   * Die Lieferbeziehung fuer „EXW ✎" in der Strecke -- nur wenn es GENAU
   * EINE gibt. Die Kalkulation nimmt den juengsten EXW-Preis ueber alle
   * Beziehungen; bei mehreren waere unklar, an welcher der neue stehen soll.
   * Dann wird er in der Lieferantenkarte erfasst, wo die Beziehung sichtbar ist.
   */
  const [exwBeziehung, setExwBeziehung] = useState<number | null>(null);
  const [fuerModell, setFuerModell] = useState(false);
  const [bestaetigt, setBestaetigt] = useState(false);
  const [serverKonflikte, setServerKonflikte] =
    useState<readonly { articleNumber: string; amountEur: string; validFrom: string }[] | null>(null);
  /*
   * DER RECHENWEG IST ZU -- DIE KETTE IST OFFEN.
   *
   * Dreissig Zeilen Saetze und Zwischenergebnisse beantworten, WIE gerechnet
   * wurde. Wer die Akte liest, fragt zuerst, WAS herauskommt und woran es
   * haengt; das sagt die Kette in einer Zeile. Der Rechenweg bleibt
   * vollstaendig und unveraendert -- einen Klick weiter.
   */
  const [rechenweg, setRechenweg] = useState(false);
  /* Die Listenpreiskalkulation im Reiter beginnt zugeklappt (Ergebnis zuerst). */
  const [kalkOffen, setKalkOffen] = useState(false);

  const laden = useCallback(async () => {
    try {
      const [c, p] = await Promise.all([
        api.articleCalculation(articleId), api.calculationParameters(),
      ]);
      setCalc(c); setParams(p);
    } catch (e) { setError(e); }
    if (mayRelease) {
      try { setScope(await api.modelReleaseScope(articleId)); } catch { setScope(null); }
    }
    if (mayCalculate && ansicht === "arbeitsflaeche") {
      try {
        const b = (await api.articleLogistics(articleId)).supplierArticles.filter((r) => r.purchasing !== null);
        setExwBeziehung(b.length === 1 ? b[0]!.id : null);
      } catch { setExwBeziehung(null); }
    }
  }, [articleId, mayRelease, mayCalculate, ansicht]);
  useEffect(() => { setCalc(null); void laden(); }, [laden]);

  async function rechnen(): Promise<void> {
    setBusy(true);
    /* Die zuletzt gewaehlte Transportart bleibt -- ohne Angabe rechnete der
       Server mit „train" und haette eine gewaehlte Seefracht still ersetzt. */
    try {
      await api.calculate(articleId, stand?.inputs.transportMode ? { transportMode: stand.inputs.transportMode } : {});
      await laden(); onPreisGeaendert?.();
    }
    catch (e) { setError(e); } finally { setBusy(false); }
  }

  /* EXW ✎ -- eine neue Preisstufe an der einen Beziehung, dann neu rechnen. */
  async function exwSpeichern(betrag: string, waehrung: string, ab: string): Promise<void> {
    setError(null);
    try {
      await api.setSupplierPrice(exwBeziehung!, {
        amount: Number(betrag.replace(",", ".")), currency: waehrung, validFrom: ab });
      await api.calculate(articleId, stand?.inputs.transportMode ? { transportMode: stand.inputs.transportMode } : {});
      await laden(); onPreisGeaendert?.();
    } catch (e) { setError(e); throw e; }
  }
  /*
   * Zielmarge / Provision / Rabattbasis ✎ -- an der QUELLE (UX-Nachtrag 002).
   *
   * Steht fuer die primaere Familie eine Abweichung, wird sie geaendert und
   * gilt fuer die ganze Familie. Sonst entsteht ein neuer zentraler
   * Parametersatz ab dem gewaehlten Tag. Einen Artikelwert gibt es nicht.
   * Danach wird dieser Artikel neu gerechnet -- mit derselben Transportart.
   */
  const satzFamilie = (code: string): { id: number; code: string } | null => {
    const f = calc?.primaryFamily ?? null;
    if (f === null || params === null) return null;
    return params.familyOverrides.some((o) => o.familyId === f.id && o.parameterCode === code) ? f : null;
  };
  async function satzSpeichern(code: string, wert: number, ab: string): Promise<void> {
    setError(null);
    try {
      const f = satzFamilie(code);
      if (f !== null) await api.setFamilyOverride({ familyId: f.id, parameterCode: code, value: wert });
      /* Zentral: DERSELBE Weg wie die Parameterseite -- eine neue Revision (0059). */
      else await api.createParameterSet({ validFrom: ab, values: [{ parameterCode: code, value: wert }] });
      await api.calculate(articleId, stand?.inputs.transportMode ? { transportMode: stand.inputs.transportMode } : {});
      await laden(); onPreisGeaendert?.();
    } catch (e) { setError(e); throw e; }
  }
  /* Fracht ✎ -- nur die Transportart; der Satz kommt aus dem Parametersatz. */
  async function frachtSpeichern(art: string): Promise<void> {
    setError(null);
    try {
      await api.calculate(articleId, { transportMode: art });
      await laden(); onPreisGeaendert?.();
    } catch (e) { setError(e); throw e; }
  }

  /* Gegen den zweiten Klick eines Doppelklicks -- der Server wuerde ihn mit
     release_exists abweisen, und die Meldung stuende nach dem Erfolg da. */
  const laeuftFreigabe = useRef(false);
  async function freigeben(): Promise<void> {
    if (laeuftFreigabe.current) return;
    laeuftFreigabe.current = true;
    setBusy(true);
    try {
      await api.releasePrice(articleId, {
        calculationId: stand?.id ?? null,
        listPriceEur: Number(preis.replace(",", ".")), validFrom: ab,
        note: notiz.trim() === "" ? null : notiz.trim(),
        belowMinimumNote: grund.trim() === "" ? null : grund.trim(),
        ...(modellAktiv ? { applyToModel: true, confirmConflicts: bestaetigt } : {}),
      });
      setFreigabe(false); setPreis(""); setAb(""); setNotiz("");
      setUnterschreitung(null); setGrund("");
      setFuerModell(false); setBestaetigt(false); setServerKonflikte(null);
      await laden();
      onPreisGeaendert?.();
    } catch (e) {
      /*
       * Die Unterschreitung ist KEIN Fehler der Bedienung, sondern eine
       * Rueckfrage. Sie erscheint deshalb als Hinweis am Formular und nicht
       * als rote Fehlermeldung darunter.
       */
      /* Der Server kennt den letzten Stand -- seine Konfliktliste gilt. */
      if (e instanceof ApiError && e.code === "model_release_conflicts"
          && Array.isArray(e.details?.["conflicts"])) {
        setServerKonflikte(e.details!["conflicts"] as never);
        setBestaetigt(false);
        return;
      }
      const gesperrt = e instanceof ApiError && e.code === "minimum_margin_forbidden";
      const fehlendeBegruendung = e instanceof ApiError && e.code === "below_minimum_margin";
      const d = gesperrt || fehlendeBegruendung ? (e as ApiError).details : null;
      if (d && typeof d["resultingMarginPercent"] === "string"
            && typeof d["minimumMarginPercent"] === "string") {
        setUnterschreitung({
          resulting: d["resultingMarginPercent"], minimum: d["minimumMarginPercent"],
          gesperrt,
        });
      } else setError(e);
    } finally { setBusy(false); laeuftFreigabe.current = false; }
  }

  /** Der juengste Stand. Aeltere bleiben lesbar, stehen aber nicht vorn. */
  const stand = calc?.calculations[0] ?? null;
  /* Die Preisstufen dieses Stands -- mit derselben Funktion wie die Engine. */
  const stufen = stand !== null && stand.steps.costBaseEur !== null && stand.inputs.minimumMarginPercent !== null
    && stand.inputs.commissionPercent !== null && stand.inputs.listPriceDivisor !== null
    && stand.inputs.roundingStepEur !== null
    ? preisstufen(Number(stand.steps.costBaseEur), Number(stand.inputs.minimumMarginPercent),
                  Number(stand.inputs.commissionPercent), Number(stand.inputs.listPriceDivisor),
                  Number(stand.inputs.roundingStepEur))
    : null;
  /* Die Modellfreigabe gibt es nur, wo das Modell mehr als eine Variante hat
     und dieser Artikel eine davon ist. */
  const modellMoeglich = scope !== null && scope.skus.length > 1
    && scope.skus.some((x) => x.articleId === articleId);
  const modellAktiv = modellMoeglich && fuerModell;
  const eingabePreis = Number(preis.replace(",", "."));
  /*
   * DIE MARGENPRUEFUNG JE AUSFUEHRUNG (Produktmodelle 002).
   *
   * Ein gemeinsamer Preis ist nur dann eine Entscheidung, wenn man sieht, was
   * er an JEDER Ausfuehrung bedeutet. Gerechnet wird mit derselben Funktion
   * wie im Server (`margeNachProvision`, `erreichtMindestmarge`) gegen den
   * juengsten vollstaendigen Stand der jeweiligen SKU. Ist eine Ausfuehrung
   * nicht pruefbar, ist die Modellfreigabe gesperrt -- die Marge NULL ist
   * hier keine Freigabe. Die Einzelfreigabe bleibt davon unberuehrt.
   */
  const margenpruefung = !modellAktiv ? [] : scope!.skus.map((x) => {
    const l = x.check.latest;
    if (!x.check.verifiable || l === null || l.costBaseEur === null || l.commissionPercent === null
        || l.listPriceDivisor === null || l.minimumMarginPercent === null) {
      return { sku: x, kosten: null, marge: null, ziel: l?.minimumMarginPercent ?? null, ok: null };
    }
    const gueltig = Number.isFinite(eingabePreis) && eingabePreis > 0;
    return {
      sku: x, kosten: l.costBaseEur, ziel: l.minimumMarginPercent,
      marge: gueltig ? margeNachProvision(eingabePreis, Number(l.costBaseEur),
                                          Number(l.commissionPercent), Number(l.listPriceDivisor)) : null,
      ok: gueltig ? erreichtMindestmarge(eingabePreis, Number(l.costBaseEur), Number(l.minimumMarginPercent),
                                         Number(l.commissionPercent), Number(l.listPriceDivisor)) : null,
    };
  });
  const nichtPruefbar = margenpruefung.filter((m) => !m.sku.check.verifiable);
  /* Der erste Grund, warum „Listenpreis freigeben" gesperrt ist -- sichtbar, nicht still. */
  const freigabeGrundVon = (k: readonly unknown[]): string | null =>
      preis.trim() === "" || !(Number(preis.replace(",", ".")) > 0) ? t("form.why.priceMissing")
    : ab === "" ? t("form.why.dateMissing")
    : modellAktiv && nichtPruefbar.length > 0 ? t("form.why.modelNotVerifiable", { n: nichtPruefbar.length })
    : unterschreitung !== null && unterschreitung.gesperrt ? t("form.why.marginBlocked")
    : unterschreitung !== null && grund.trim() === "" ? t("form.why.reasonMissing")
    : modellAktiv && k.length > 0 && !bestaetigt ? t("form.why.conflictsUnconfirmed")
    : null;
  const konflikte = !modellAktiv ? [] : serverKonflikte ?? scope!.skus.flatMap((x) =>
    [...(x.current ? [x.current] : []), ...x.upcoming]
      .filter((r) => Number.isFinite(eingabePreis) && Number(r.amountEur) !== eingabePreis)
      .map((r) => ({ articleNumber: x.articleNumber, ...r })));
  const freigabeGrund = freigabeGrundVon(konflikte);
  const satz = params?.sets.find((s) => s.id === stand?.parameterSetId) ?? null;

  const zahl = (v: string | null): React.ReactNode =>
    v === null ? <Unknown /> : betrag(v, i18n.lang);

  /* ── Die Bausteine -- einmal beschrieben, in zwei Anordnungen gesetzt. ── */
  const bParametersatz = stand === null ? null : (
              <Fact label={t("akte.parameterSet")}>
                {t("akte.validFromRevision", { date: stand.parameterValidFrom, revision: stand.parameterRevision })}
                {satz?.note && <span className="akte__since"> · {satz.note}</span>}
              </Fact>);
  const bParameter = stand === null ? null : (<>

              <Fact label={t("param.usd_eur_rate")}>{zahl(stand.inputs.usdEurRate)}</Fact>
              <Fact label={t("param.usd_eur_safety_discount")} herkunft="vorgabe">
                {zahl(stand.inputs.usdEurSafetyDiscount)}
              </Fact>
              {/*
                * DER KURS, MIT DEM GERECHNET WURDE -- und nicht der vom
                * Schwarzen Brett. Bis 0055 rechnete die Kette mit dem
                * Referenzkurs; der Unterschied von 0,05 sind an einem Landed
                * Cost von 62,77 USD zweieinhalb Euro je Stueck. Die Zeile
                * nennt die Herleitung mit, damit die Abweichung zum
                * Referenzkurs darueber nicht wie ein Fehler aussieht.
                */}
              <Fact label={t("param.usd_eur_calc_rate")} herkunft="ergebnis"
                    quelle={stand.inputs.usdEurRate && stand.inputs.usdEurSafetyDiscount
                      ? t("param.calcRateFrom", {
                          ref: kurs(stand.inputs.usdEurRate, i18n.lang),
                          ab: kurs(stand.inputs.usdEurSafetyDiscount, i18n.lang) })
                      : undefined}>
                {zahl(stand.inputs.usdEurCalcRate)}
              </Fact>
              {/*
                * WELCHE TRANSPORTART -- seit es drei gibt, ist das keine
                * Selbstverstaendlichkeit mehr. Ohne die Zeile waere bei drei
                * moeglichen Saetzen nicht erkennbar, welcher in den Preis
                * eingegangen ist.
                */}
              <Fact label={t("akte.transportMode")} herkunft="eingabe">
                {stand.inputs.transportMode === null
                  ? <Unknown />
                  /* `tCode` statt einer zusammengesetzten Zeichenkette: die
                     Pruefung der Sprachdateien sieht dynamische Schluessel
                     sonst nicht und meldet sie als unbenutzt. */
                  : i18n.tCode("transport", stand.inputs.transportMode)}
              </Fact>
              <Fact label={t("akte.freightRate")} herkunft="vorgabe">
                {stand.inputs.freightRateUsd === null
                  ? <Unknown />
                  : <>{zahl(stand.inputs.freightRateUsd)}{" "}
                      {stand.inputs.transportMode === "air" ? "USD/kg" : "USD/cbm"}</>}
              </Fact>
              <Fact label={t("param.csef_percent")} herkunft="vorgabe">{zahl(stand.inputs.csefPercent)} %</Fact>
              <Fact label={t("param.commission_percent")} herkunft="vorgabe">
              {stand.inputs.commissionPercent === null
                ? <Unknown /> : prozent(stand.inputs.commissionPercent, i18n.lang)} %
            </Fact>
              {/*
                * Die Saetze stehen bei den uebrigen Vorgaben und nicht am
                * Artikel: sie gelten fuer das ganze Haus und aendern sich fuer
                * alle Produkte zugleich. An jedem Artikel gepflegt waeren sie
                * dreizehnhundertmal dieselbe Zahl -- und beim naechsten
                * Satzwechsel dreizehnhundert Stellen, von denen eine vergessen
                * wird.
                *
                * Angezeigt wird EUR je Tonne, gerechnet wird in EUR je
                * Kilogramm. Das ist kein Widerspruch: 237,00 EUR/t ist die
                * Zahl, die im Haus genannt wird, und 0,237 EUR/kg dieselbe
                * Zahl in der Einheit der Mengen. Wer nur eine der beiden
                * saehe, muesste die andere im Kopf umrechnen.
                */}
              <Fact label={t("param.packaging_act_cardboard_eur_per_kg")} herkunft="vorgabe">
                {stand.inputs.packagingActCardboardEurPerKg === null
                  ? <Unknown />
                  : <>{geld(String(Number(stand.inputs.packagingActCardboardEurPerKg) * 1000), i18n.lang)} EUR/t
                      <span className="akte__since"> · {zahl(stand.inputs.packagingActCardboardEurPerKg)} EUR/kg</span></>}
              </Fact>
              <Fact label={t("param.packaging_act_plastic_eur_per_kg")} herkunft="vorgabe">
                {stand.inputs.packagingActPlasticEurPerKg === null
                  ? <Unknown />
                  : <>{geld(String(Number(stand.inputs.packagingActPlasticEurPerKg) * 1000), i18n.lang)} EUR/t
                      <span className="akte__since"> · {zahl(stand.inputs.packagingActPlasticEurPerKg)} EUR/kg</span></>}
              </Fact>
              {/*
                * ElektroG traegt sein Wesen im Namen der Zeile: es ist heute
                * ein KALKULATORISCHER UMLAGESATZ. greenius hatte bisher keine
                * EAR-Abholung -- ihn als ermittelten Abholpreis zu zeigen
                * waere eine Behauptung ueber eine Erfahrung, die es nicht gibt.
                */}
              <Fact label={t("param.electro_act_eur_per_kg")} herkunft="vorgabe"
                    quelle={t("akte.electroActNote")}>
                {stand.inputs.electroActEurPerKg === null
                  ? <Unknown /> : <>{zahl(stand.inputs.electroActEurPerKg)} EUR/kg</>}
              </Fact>
              </>);
  const bFehlend = stand === null ? null : (<>
              {stand.missingInputs.length > 0 && (
                <p className="akte__missing">
                  {t("akte.missing", {
                    items: stand.missingInputs
                      .map((m) => i18n.tCode("calcInput", m, m)).join(", "),
                  })}
                </p>
              )}
  </>);
  const bRechenweg = stand === null ? null : (<>

                <Fact label={t("akte.exw")} herkunft="eingabe">
                  {stand.inputs.exwAmount === null
                    ? <Unknown /> : geld(stand.inputs.exwAmount, i18n.lang)} {stand.inputs.exwCurrency ?? ""}
                </Fact>
                <Fact label={t("akte.volumePerUnit")} herkunft="eingabe">
                  {stand.inputs.volumeM3PerUnit === null
                    ? <Unknown /> : volumen(stand.inputs.volumeM3PerUnit, i18n.lang)} cbm
                </Fact>
                <Fact label={t("akte.freight")} herkunft="ergebnis">
                  {stand.steps.freightUsd === null
                    ? <Unknown /> : geld(stand.steps.freightUsd, i18n.lang)} USD
                </Fact>
                {/* Luftfracht: die festgehaltene Herleitung aus dem Masterkarton (0058). */}
                {stand.inputs.air !== null && <LuftFakten air={stand.inputs.air} i18n={i18n} />}
                <Fact label={t("akte.csef")} herkunft="ergebnis">
                  {stand.steps.csefUsd === null
                    ? <Unknown /> : geld(stand.steps.csefUsd, i18n.lang)} USD
                </Fact>
                <Fact label={t("akte.duty")} herkunft="ergebnis"
                      quelle={stand.inputs.tariffCode
                        ? t("akte.dutyFrom", {
                            rate: prozent(stand.inputs.dutyRatePercent ?? "0", i18n.lang),
                            code: stand.inputs.tariffCode })
                        : undefined}>
                  {stand.steps.dutyUsd === null
                    ? <Unknown /> : geld(stand.steps.dutyUsd, i18n.lang)} USD
                </Fact>
                <Fact label={t("akte.landedUsd")} herkunft="ergebnis">
                  {stand.steps.landedCostUsd === null
                    ? <Unknown /> : <strong>{geld(stand.steps.landedCostUsd, i18n.lang)}</strong>} USD
                </Fact>
                {/*
                  * DER KURS, MIT DEM GERECHNET WURDE.
                  *
                  * Hier stand der Referenzkurs -- die Zeile sagte „Kurs 1,16",
                  * waehrend die Zahl daneben mit 1,11 entstanden war. Eine
                  * Herleitung, die eine andere Zahl nennt als die, aus der das
                  * Ergebnis stammt, ist schlimmer als keine: sie laedt dazu
                  * ein, den Betrag nachzurechnen und dem Ergebnis zu
                  * misstrauen.
                  */}
                <Fact label={t("akte.landedEur")} herkunft="ergebnis"
                      quelle={stand.inputs.usdEurCalcRate
                        ? t("akte.atRate", { rate: kurs(stand.inputs.usdEurCalcRate, i18n.lang) })
                        : undefined}>
                  {stand.steps.landedCostEur === null
                    ? <Unknown /> : <strong>{geld(stand.steps.landedCostEur, i18n.lang)}</strong>} EUR
                </Fact>
                {/*
                  * VERPACKUNGSG UND ELEKTROG -- zwei reale Kosten je Produkt.
                  *
                  * Sie stehen NACH dem Landed Cost, weil sie Euro-Betraege
                  * sind, und VOR der Marge, weil sie Kosten sind wie jede
                  * andere. Die Herleitung steht daneben: Menge mal Satz. Ohne
                  * sie waere „0,43 EUR" eine Zahl, die man glauben muss.
                  */}
                <Fact label={t("akte.packagingAct")} herkunft="ergebnis"
                      quelle={herleitungVerpackung(stand, i18n)}>
                  {stand.steps.packagingActEur === null
                    ? <Unknown /> : <strong>{geld(stand.steps.packagingActEur, i18n.lang)}</strong>} EUR
                </Fact>
                <Fact label={t("akte.electroAct")} herkunft="ergebnis"
                      quelle={stand.inputs.productWeightKg && stand.inputs.electroActEurPerKg
                        ? t("akte.electroActFrom", {
                            kg: zahlMit(stand.inputs.productWeightKg, i18n.lang, 2),
                            rate: zahlMit(stand.inputs.electroActEurPerKg, i18n.lang, 2) })
                        : undefined}>
                  {stand.steps.electroActEur === null
                    ? <Unknown /> : <strong>{geld(stand.steps.electroActEur, i18n.lang)}</strong>} EUR
                </Fact>
                {/*
                  * Die Kostenbasis ist die Zahl, auf die Marge, Provision und
                  * Divisor angewandt werden -- und die Zahl, gegen die die
                  * Freigabe ihre Marge misst. Sie steht hier, damit die Kette
                  * ohne Kopfrechnen lesbar bleibt.
                  */}
                <Fact label={t("akte.costBase")} herkunft="ergebnis">
                  {stand.steps.costBaseEur === null
                    ? <Unknown /> : <strong>{geld(stand.steps.costBaseEur, i18n.lang)}</strong>} EUR
                </Fact>
              </>);
  const bZielmarge = stand === null ? null : (<>

            {/*
              * ZIELMARGE (Nachtrag 02, 19.09.2026). Die Quellspalte heisst
              * „Minimum Margin (%)"; fachlich ist es die Marge, die nach
              * Provision, Rabattbasis und finaler Rundung mindestens bleiben
              * muss. Sichtbar heisst sie Zielmarge, das Datenfeld behaelt
              * seinen Namen.
              */}
            <Fact label={t("akte.minimumMargin")} herkunft="vorgabe">
              {stand.inputs.minimumMarginPercent === null
                ? <Unknown /> : satzKurz(stand.inputs.minimumMarginPercent, i18n.lang)} %
              {/*
                * Aus welcher Familie sie stammt. Ohne diese Angabe stuende
                * hier eine 55 und niemand koennte sagen, warum sie nicht
                * 50 ist.
                */}
              <span className="akte__since">
                {" "}{stand.inputs.marginFromFamily
                  ? t("akte.marginFromFamily", { family: stand.inputs.marginFromFamily })
                  : t("akte.marginStandard")}
              </span>
              {/*
                * WARUM HIER KEIN EINGABEFELD STEHT.
                *
                * Die Zielmarge ist keine Artikelangabe. Sie gilt global
                * oder je Produktfamilie -- ein Feld am Artikel behauptete das
                * Gegenteil und liesse 357 Stellen entstehen, an denen
                * dieselbe Groesse anders lauten kann.
                *
                * Der Satz erklaert das, statt den Benutzer raten zu lassen,
                * warum er nichts anklicken kann.
                */}
              <span className="akte__quelle">{t("akte.marginWhereEdited")}</span>
            </Fact>
            {/*
              * DIE STUFEN ZWISCHEN KOSTENBASIS UND LISTENPREIS -- mit derselben
              * Funktion gerechnet wie in der Engine, damit die Erklaerung nie
              * etwas anderes sagt als die Rechnung.
              */}
            {stufen !== null && (
              <Fact label={t("stufen.netBeforeCommission")} herkunft="ergebnis"
                    quelle={t("stufen.netBeforeCommissionFrom", {
                      k: geld(stand.steps.costBaseEur!, i18n.lang),
                      m: satzKurz(stand.inputs.minimumMarginPercent!, i18n.lang) })}>
                {geld(String(stufen.nettoerloesNachMarge), i18n.lang)} EUR
              </Fact>
            )}
          </>);
  const bProvision = stand === null ? null : (<>
            <Fact label={t("param.commission_percent")} herkunft="vorgabe"
                  quelle={t("stufen.commissionMeaning")}>
              {stand.inputs.commissionPercent === null
                ? <Unknown /> : satzKurz(stand.inputs.commissionPercent, i18n.lang)} %
            </Fact>
            {stufen !== null && (<>
              <Fact label={t("stufen.salesNet")} herkunft="ergebnis"
                    quelle={t("stufen.salesNetFrom", { p: satzKurz(stand.inputs.commissionPercent!, i18n.lang) })}>
                {geld(String(stufen.nettoverkaufspreis), i18n.lang)} EUR
              </Fact>
              <Fact label={t("stufen.commissionAmount")} herkunft="ergebnis"
                    quelle={t("stufen.commissionAmountFrom", {
                      p: satzKurz(stand.inputs.commissionPercent!, i18n.lang),
                      s: geld(String(stufen.nettoverkaufspreis), i18n.lang) })}>
                {geld(String(stufen.provision), i18n.lang)} EUR
              </Fact>
              <Fact label={t("stufen.netAfterCommission")} herkunft="ergebnis"
                    quelle={t("stufen.netAfterCommissionMeaning", { m: satzKurz(stand.inputs.minimumMarginPercent!, i18n.lang) })}>
                {geld(String(stufen.nettoerloesNachProvision), i18n.lang)} EUR
              </Fact>
            </>)}
          </>);
  /*
   * RABATTBASIS -- was der Divisor bedeutet: 0,50 heisst 50 % Rabattbasis und
   * Listenpreisfaktor x 2. Drei Schreibweisen derselben Zahl, damit niemand
   * umrechnen muss.
   */
  const divisorGueltig = stand !== null && stand.inputs.listPriceDivisor !== null
    && Number(stand.inputs.listPriceDivisor) > 0 && Number(stand.inputs.listPriceDivisor) <= 1;
  const bRabattbasis = stand === null ? null : (<>
            <Fact label={t("strecke.discountBasis")} herkunft="vorgabe">
              {divisorGueltig
                ? `${satzKurz(String((1 - Number(stand.inputs.listPriceDivisor)) * 100), i18n.lang)} %`
                : <Unknown />}
            </Fact>
            {/* DIVISOR, nicht Faktor: 0,5 bedeutet Verdopplung. Der Name steht
                hier so, wie gerechnet wird (Migration 0018). */}
            <Fact label={t("param.list_price_divisor")} herkunft="vorgabe"
                  quelle={divisorGueltig
                    ? t("strecke.discountBasisFrom", {
                        basis: satzKurz(String((1 - Number(stand.inputs.listPriceDivisor)) * 100), i18n.lang),
                        faktor: zahlMit(String(1 / Number(stand.inputs.listPriceDivisor)), i18n.lang, 0) })
                    : undefined}>
              {stand.inputs.listPriceDivisor === null
                ? <Unknown /> : kurs(stand.inputs.listPriceDivisor, i18n.lang)}
            </Fact>
            <Fact label={t("stufen.listPriceFactor")} herkunft="ergebnis">
              {divisorGueltig
                ? `× ${zahlMit(String(1 / Number(stand.inputs.listPriceDivisor)), i18n.lang, 0)}`
                : <Unknown />}
            </Fact>
          </>);
  /*
   * RUNDUNG -- kaufmaennisch auf den Rundungsschritt; nur wenn das die
   * Zielmarge unterschritte, auf den naechsten Schritt erhoeht (E1). Die
   * Marge nach Rundung steht daneben, gerechnet mit derselben Funktion wie
   * die Freigabepruefung.
   */
  const margeGerundet = stand === null || stand.calculatedListPriceEur === null
    || stand.steps.costBaseEur === null || stand.inputs.commissionPercent === null
    || !divisorGueltig
    ? null
    : margeNachProvision(Number(stand.calculatedListPriceEur), Number(stand.steps.costBaseEur),
        Number(stand.inputs.commissionPercent), Number(stand.inputs.listPriceDivisor));
  const bRundung = stand === null ? null : (<>
            <Fact label={t("param.rounding_step_eur")} herkunft="vorgabe">
              {stand.inputs.roundingStepEur === null
                ? <Unknown /> : <>{geld(stand.inputs.roundingStepEur, i18n.lang)} EUR</>}
            </Fact>
            {stufen !== null && (
              <Fact label={t("stufen.listPriceRaw")} herkunft="ergebnis">
                {zahlMit(String(stufen.listenpreisRoh), i18n.lang, 4)} EUR
              </Fact>
            )}
            <Fact label={t("akte.calculatedListPrice")} herkunft="ergebnis"
                  quelle={stufen === null || stand.inputs.roundingStepEur === null
                    || stand.inputs.minimumMarginPercent === null
                    ? undefined
                    : t(stufen.aufgerundet ? "stufen.roundedUp" : "stufen.roundedNormal", {
                        step: geld(stand.inputs.roundingStepEur, i18n.lang),
                        m: satzKurz(stand.inputs.minimumMarginPercent, i18n.lang) })}>
              {stand.calculatedListPriceEur === null
                ? <Unknown />
                : <strong className="akte__price">
                    {geld(stand.calculatedListPriceEur, i18n.lang)} EUR
                  </strong>}
            </Fact>
            {margeGerundet !== null && (
              <Fact label={t("stufen.marginAfterRounding")} herkunft="ergebnis">
                {zahlMit(String(margeGerundet), i18n.lang, 2)} %
              </Fact>
            )}
          </>);
  const bVerkauf = stand === null ? null : (<>{bZielmarge}{bProvision}{bRabattbasis}{bRundung}</>);
  const bFreigaben = (<>
        {calc?.releases.map((r) => (
          <div key={r.id}>
            <dl className="akte__facts">
              <Fact label={t("akte.releasedPrice")}>
                <strong className="akte__price">{zahl(r.listPriceEur)} EUR</strong>{" "}
                <span className="akte__since">{t("akte.validFromDate", { date: r.validFrom })}</span>
              </Fact>
              <Fact label={t("akte.releasedBy")}>
                {r.releasedBy}
                {r.note && <span className="akte__since"> · {r.note}</span>}
              </Fact>
              {/* Die erzielte Marge steht bei jeder Freigabe -- nicht nur bei
                  den auffaelligen. Nur so ist sie vergleichbar. */}
              {r.calculatedListPriceEur !== null && (
                <Fact label={t("akte.calculatedListPrice")}>
                  {zahl(r.calculatedListPriceEur)} EUR
                </Fact>
              )}
              {r.resultingMarginPercent !== null && (
                <Fact label={t("akte.resultingMargin")}>
                  {zahl(r.resultingMarginPercent)} %
                  {r.minimumMarginPercent !== null && (
                    <span className="akte__since">
                      {" "}{t("akte.minimumWas", { value: betrag(r.minimumMarginPercent, i18n.lang) })}
                    </span>
                  )}
                </Fact>
              )}
            </dl>
            {/* Eine bewusste Unterschreitung bleibt sichtbar, solange es die
                Freigabe gibt. Sie war eine Entscheidung, kein Versehen. */}
            {r.belowMinimumNote !== null && (
              <p className="akte__missing">
                <strong>{t("akte.exceptionRecorded")}</strong>{" "}
                {t("akte.belowMinimumRecorded", {
                  margin: r.resultingMarginPercent === null
                    ? "?" : prozent(r.resultingMarginPercent, i18n.lang),
                  minimum: r.minimumMarginPercent === null
                    ? "?" : prozent(r.minimumMarginPercent, i18n.lang),
                })}
                {" "}{r.belowMinimumNote}
              </p>
            )}
          </div>
        ))}
  </>);
  /*
   * DIE MODELLOPTION STEHT AM LISTENPREIS -- vor und im Freigabeformular.
   *
   * Sichtbar, bevor jemand „freigeben" drueckt: die Entscheidung „nur diese
   * SKU oder alle Varianten von HB200-U" gehoert zur Freigabe, nicht in einen
   * zweiten Schritt. Die Konflikte erscheinen, sobald ein Preis dasteht --
   * je SKU mit bisherigem und neuem Preis.
   */
  const modellBlock = !(mayRelease && modellMoeglich) ? null : (
    <div className="freigabe__modell">
      <label className="freigabe__check">
        <input type="checkbox" checked={fuerModell}
          onChange={(e) => { setFuerModell(e.target.checked); setBestaetigt(false); setServerKonflikte(null); }} />
        {t("release.forModel", { model: scope!.model.code })}
      </label>
      <span className="freigabe__umfang">{t("release.appliesTo", { n: scope!.skus.length })}</span>
      {modellAktiv && (
        <div className="freigabe__pruefung" data-testid="freigabe-pruefung">
          <p className="akte__blockedline"><strong>{t("release.previewTitle")}</strong></p>
          {nichtPruefbar.length > 0 && (
            <p className="akte__blockedline freigabe__nichtpruefbar" role="alert">
              {t("release.notVerifiable", { n: nichtPruefbar.length })}
            </p>
          )}
          <table className="freigabe__tabelle">
            <thead>
              <tr>
                <th>{t("release.colSku")}</th><th>{t("release.colCostBase")}</th>
                <th>{t("release.colPrice")}</th><th>{t("release.colMargin")}</th>
                <th>{t("release.colTarget")}</th><th>{t("release.colStatus")}</th>
              </tr>
            </thead>
            <tbody>
              {margenpruefung.map((m) => (
                <tr key={m.sku.articleId} data-testid="freigabe-pruefzeile">
                  <td>{m.sku.articleNumber}</td>
                  <td>{m.kosten !== null ? geld(m.kosten, i18n.lang) : "—"}</td>
                  <td>{Number.isFinite(eingabePreis) && eingabePreis > 0 ? geld(String(eingabePreis), i18n.lang) : "—"}</td>
                  <td>{m.marge !== null ? `${prozent(m.marge.toFixed(2), i18n.lang)} %` : "—"}</td>
                  <td>{m.ziel !== null ? `${prozent(m.ziel, i18n.lang)} %` : "—"}</td>
                  <td className={m.sku.check.verifiable
                    ? (m.ok === false ? "freigabe__status freigabe__status--unter" : "freigabe__status freigabe__status--ok")
                    : "freigabe__status freigabe__status--gesperrt"}>
                    {!m.sku.check.verifiable
                      ? <>{t("release.statusNotVerifiable")}
                          <span className="freigabe__gruende">
                            {" "}({[...m.sku.check.blockers.map((b) => t(BLOCKER_KEY[b as keyof typeof BLOCKER_KEY] ?? "mp.blocker.basis_incomplete")),
                                   ...m.sku.check.missing.map((c) => i18n.tCode("calcInput", c, c))].join(", ")})
                          </span></>
                      : m.ok === null ? "—"
                      : m.ok ? t("release.statusOk") : t("release.statusBelow")}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {freigabe && modellAktiv && konflikte.length > 0 && (
        <div className="akte__missing freigabe__konflikte" role="alert">
          <p className="akte__blockedline">
            <strong>{t("release.conflictsTitle", { n: konflikte.length })}</strong>{" "}
            {t("release.conflictsText")}
          </p>
          <ul>
            {konflikte.map((k) => (
              <li key={`${k.articleNumber}-${k.validFrom}`}>
                {t("release.conflictLine", {
                  sku: k.articleNumber,
                  alt: geld(k.amountEur, i18n.lang),
                  neu: Number.isFinite(eingabePreis) ? geld(String(eingabePreis), i18n.lang) : "?",
                  date: k.validFrom,
                })}
              </li>
            ))}
          </ul>
          <label className="freigabe__check">
            <input type="checkbox" checked={bestaetigt}
              onChange={(e) => setBestaetigt(e.target.checked)} />
            {t("release.confirmConflicts")}
          </label>
        </div>
      )}
    </div>
  );
  const bFreigabeForm = (<>
        {mayRelease && (freigabe
          ? <>
              {/*
                * DIE WARNUNG KOMMT VOM SERVER, NICHT VON HIER.
                *
                * Die erzielte Marge wird NICHT im Browser gerechnet -- das
                * waere eine zweite Rechenwahrheit neben dem Kalkulationskern.
                * Stattdessen weist der Server die Freigabe ab (409) und
                * liefert beide Zahlen mit; erst dann erscheint dieser Hinweis
                * und das Begruendungsfeld. Ein Klick mehr, dafuer eine Zahl,
                * die stimmt.
                */}
              {unterschreitung !== null && (
                /*
                 * Zwei Faelle, zwei Texte -- und sachlich, nicht dramatisch.
                 * Wer nicht darf, erfaehrt das sofort und wird nicht erst zum
                 * Schreiben einer Begruendung aufgefordert.
                 */
                <div className={unterschreitung.gesperrt ? "akte__blocked" : "akte__missing"}
                     role="alert">
                  <p className="akte__blockedline">
                    <strong>{t(unterschreitung.gesperrt
                      ? "akte.belowMinimumBlocked" : "akte.belowMinimumTitle")}</strong>{" "}
                    {t(unterschreitung.gesperrt
                      ? "akte.belowMinimumBlockedText" : "akte.belowMinimumWarning")}
                  </p>
                  <dl className="akte__facts">
                    <Fact label={t("akte.minimumMargin")}>
                      {prozent(unterschreitung.minimum, i18n.lang)} %
                    </Fact>
                    <Fact label={t("akte.resultingMargin")}>
                      <strong>{prozent(unterschreitung.resulting, i18n.lang)} %</strong>
                    </Fact>
                  </dl>
                </div>
              )}
              <div className="akte__inline">
                <label>{t("akte.releasedPrice")}
                  <input className="input" inputMode="decimal" value={preis}
                    onChange={(e) => { setPreis(e.target.value); setUnterschreitung(null);
                                       setServerKonflikte(null); setBestaetigt(false); }} />
                </label>
                <label>{t("akte.validFrom")}
                  <input className="input" type="date" value={ab} onChange={(e) => setAb(e.target.value)} />
                </label>
                <label>{t("akte.note")}
                  <input className="input" value={notiz} onChange={(e) => setNotiz(e.target.value)} />
                </label>
                {unterschreitung !== null && !unterschreitung.gesperrt && (
                  <label>{t("akte.belowMinimumReason")}
                    <input className="input" value={grund} onChange={(e) => setGrund(e.target.value)} />
                  </label>
                )}
                {/*
                  * Ohne Ausnahmerecht gibt es keinen aktiven Weg weiter. Die
                  * Schaltflaeche verschwindet nicht -- sie bleibt sichtbar und
                  * gesperrt, damit erkennbar bleibt, dass hier ein Vorgang
                  * begonnen wurde und woran er haengt.
                  */}
                {modellBlock}
                <button className="btn btn--primary kalk__freigeben"
                  disabled={busy || freigabeGrund !== null}
                  onClick={freigeben}>
                  {unterschreitung === null || unterschreitung.gesperrt
                    ? t("akte.release") : t("akte.releaseException")}
                </button>
                <button className="btn kalk__freigabeabbrechen" onClick={() => {
                  setFreigabe(false); setUnterschreitung(null); setGrund("");
                  setBestaetigt(false); setServerKonflikte(null);
                  setPreis(""); setAb(""); setNotiz(""); setError(null);
                }}>{t("common.cancel")}</button>
                {freigabeGrund !== null && (
                  <span className="akte__sperrgrund" role="status">{freigabeGrund}</span>
                )}
              </div>
            </>
          : <>{modellBlock}<button className="btn" onClick={() => {
              setFreigabe(true);
              // Der berechnete Wert als Vorschlag -- aenderbar. Er wird
              // NICHT stillschweigend uebernommen: freigegeben wird, was
              // hier steht, wenn jemand darauf drueckt.
              setPreis(stand?.calculatedListPriceEur ?? "");
              /*
               * GUELTIG-AB IM FORMULARSTATE (Gueltig-ab-Formularstate 001).
               * Bis hierher blieb das Datum leer; Safari zeigte trotzdem den
               * heutigen Tag, und „Listenpreis freigeben" blieb grau --
               * produktiv am 19.09.2026. Jetzt steht heute wirklich im Formular.
               */
              setAb(heuteLokal()); setNotiz(""); setGrund(""); setUnterschreitung(null);
              setBestaetigt(false); setServerKonflikte(null); setError(null);
            }}>{t("akte.release")}</button></>)}
  </>);
  const bAktionen = (
        <div className="akte__actions">
          {mayCalculate && (
            <button className="btn" disabled={busy} onClick={rechnen}>{t("akte.recalculate")}</button>
          )}
          {stand && (
            <span className="akte__since">
              {t("akte.calculatedAt", {
                date: new Date(stand.calculatedAt).toLocaleString(i18n.lang === "en" ? "en-GB" : "de-DE"),
                user: stand.calculatedBy ?? "—",
              })}
            </span>
          )}
        </div>);
  const bFehler = error !== null && <p className="akte__error" role="alert">{errorText(i18n, error)}</p>;

  /*
   * DIE ARBEITSFLAECHE -- EINKAUF & KALKULATION IM REITER.
   *
   * Drei Ebenen statt drei nummerierter Kapitel: die Kette (was kommt heraus
   * und woran haengt es), der Listenpreis mit seinem Zustand (berechnet oder
   * freigegeben) und -- einen Klick weiter -- die vollstaendige Grundlage.
   * Dieselben Bausteine wie in der Produktakte; nur ihre Anordnung ist eine
   * andere. Die Produktakte bleibt das vollstaendige, nummerierte Dokument.
   */
  if (ansicht === "arbeitsflaeche") {
    /*
     * DER ZUSTAND STEHT AM LISTENPREIS-GLIED -- nicht in einem zweiten Block.
     * Dieselbe Aussage traegt der Block „Listenpreis nach Importweg" oben
     * (`preisZustand`).
     */
    const status = preisZustand(calc, i18n);
    return (
      <section className={kalkOffen ? "kalk kalk--offen" : "kalk"} aria-label={t("kalk.listPriceCalc")}>
        {/*
          * ZWEI EBENEN, ZWEI BEDIENELEMENTE (UX-Nachtrag 002).
          *
          *   Chevron             zeige/verberge die Kalkulation: Kette,
          *                       Freigabe mit Modelloption, Transportvergleich
          *   Berechnungsdetails  erklaere die Rechnung: Grundlage, Rechenweg,
          *                       Zielmarge, Provision, Rabattbasis, Rundung
          *   Stift (in der Kette) diesen fachlichen Wert darf ich aendern
          *
          * Vorher hiessen Klappe und Herleitung beide „Details" -- „Details
          * ausblenden" schloss mal das eine, mal das andere.
          */}
        <header className="kalk__kopf">
          <h4 className="kalk__titel">{t("kalk.listPriceCalc")}</h4>
          <span className="kalk__aktionen">
            {stand !== null && (
              <button className="btn btn--small kette__schalter" aria-expanded={rechenweg}
                aria-controls="kalk-details" onClick={() => setRechenweg(!rechenweg)}>
                {rechenweg ? t("kalk.hideDetails") : t("kalk.showDetails")}
              </button>
            )}
            {mayCalculate && (
              <button className="btn btn--small kalk__rechnen" disabled={busy} onClick={rechnen}>
                {t("akte.recalculate")}
              </button>
            )}
            {stand !== null && (
              <button type="button" className="kalk__klappe" aria-expanded={kalkOffen}
                aria-label={kalkOffen ? t("kalk.collapse") : t("kalk.expand")}
                title={kalkOffen ? t("kalk.collapse") : t("kalk.expand")}
                onClick={() => setKalkOffen(!kalkOffen)}><IconChevron /></button>
            )}
          </span>
        </header>
        <div className="kalk__inhalt">
          {/*
            * GESCHLOSSEN: DAS ERGEBNIS. GEOEFFNET: DER WEG DORTHIN -- und die
            * Freigabe. Die Herleitung ist davon unabhaengig.
            */}
          {stand === null ? (
            <p className="kalk__status">{t("head.priceNone")}</p>
          ) : !kalkOffen ? (
            <div className="kalk__zusammenfassung">
              <span className="kalk__zpreis">
                {stand.calculatedListPriceEur === null ? <Unknown /> : <>{geld(stand.calculatedListPriceEur, i18n.lang)} EUR</>}
              </span>
              {status}
            </div>
          ) : (
            <>
              {bFehlend}
              <Strecke stand={stand} i18n={i18n} listenStatus={status}
                exwBearbeiten={mayCalculate && exwBeziehung !== null ? exwSpeichern : undefined}
                frachtAendern={mayCalculate ? frachtSpeichern : undefined}
                satzBearbeiten={mayRelease && mayCalculate
                  ? { quelle: (c) => satzFamilie(c)?.code ?? null, speichern: satzSpeichern,
                      revisionAm: (ab) => {
                        const r = (params?.sets ?? []).filter((z) => z.validFrom === ab).map((z) => z.revision);
                        return r.length === 0 ? null : Math.max(...r);
                      } } : undefined} />
            </>
          )}
          {/* Freigeben -- bewusst, mit dem berechneten Wert als aenderbarem
              Vorschlag. Freigebbar ist nur der AKTIVE Weg; der
              Transportvergleich darunter hat keinen Knopf. Ohne Kalkulation
              steht die Freigabe offen da -- es gibt nichts einzuklappen. */}
          {(kalkOffen || stand === null) && <div className="kalk__freigabe">{bFreigabeForm}</div>}
          {bFehler}

          {/*
            * DIE HERLEITUNG -- VOLLSTAENDIG, EINEN KLICK WEITER.
            *
            * Parametersatz, Kurs, Frachtrate, CSEF, Landed Cost, die beiden
            * Abgaben einzeln, Margenherkunft, Divisor, Zeitpunkt und die
            * bisherigen Freigaben. Nichts davon ist entfernt; es steht nur
            * nicht mehr vor dem Ergebnis.
            */}
          {rechenweg && stand !== null && (
            <div className="kalk__details" id="kalk-details">
              <p className="kalk__meta">
                {t("akte.calculatedAt", {
                  date: new Date(stand.calculatedAt).toLocaleString(i18n.lang === "en" ? "en-GB" : "de-DE"),
                  user: stand.calculatedBy ?? "—",
                })}
              </p>
              <h5 className="kalk__detailtitel">{t("kalk.basis")}</h5>
              <dl className="akte__facts">{bParametersatz}{bParameter}</dl>
              <h5 className="kalk__detailtitel">{t("kalk.steps")}</h5>
              <dl className="akte__facts">{bRechenweg}</dl>
              <h5 className="kalk__detailtitel">{t("akte.minimumMargin")}</h5>
              <dl className="akte__facts">{bZielmarge}</dl>
              <h5 className="kalk__detailtitel">{t("strecke.commission")}</h5>
              <dl className="akte__facts">{bProvision}</dl>
              <h5 className="kalk__detailtitel">{t("strecke.discountBasis")}</h5>
              <dl className="akte__facts">{bRabattbasis}</dl>
              <h5 className="kalk__detailtitel">{t("kalk.rounding")}</h5>
              <dl className="akte__facts">{bRundung}</dl>
              {/*
                * DER TRANSPORTVERGLEICH MIT HERLEITUNG (Importweg 001).
                *
                * Die Entscheidung -- welcher Listenpreis je Importweg -- steht
                * oben neben dem Einkauf. Hier steht, wie die Wege zustande
                * kommen: Frachtsatz, Differenz zum aktiven Weg, bei Luftfracht
                * Masterkarton und abrechenbares Gewicht. Dieselbe Komponente,
                * dieselbe Szenariorechnung.
                */}
              <Transportvergleich articleId={articleId} stand={stand} i18n={i18n} />
              <h5 className="kalk__detailtitel">{t("akte.releases")}</h5>
              {calc !== null && calc.releases.length === 0
                ? <p className="akte__note">{t("akte.noRelease")}</p>
                : bFreigaben}
            </div>
          )}
        </div>
      </section>
    );
  }

  return (
    <>
      <Section code="zuschlaege" i18n={i18n}>
        {stand === null
          ? <p className="akte__note">{t("akte.noCalculation")}</p>
          : <dl className="akte__facts">
              {bParametersatz}
              {/* Die einzelnen Saetze stehen erst mit dem Rechenweg da --
                  im Normalfall sagt die Kette unten, was sie bewirken. */}
              {rechenweg && bParameter}
            </dl>}
      </Section>

      <Section code="importkalkulation" i18n={i18n}>
        {stand === null
          ? <p className="akte__note">{t("akte.noCalculation")}</p>
          : <>
              {bFehlend}
              <Kette stand={stand} i18n={i18n} />
              <button className="btn btn--small kette__schalter" aria-expanded={rechenweg}
                onClick={() => setRechenweg(!rechenweg)}>
                {rechenweg ? t("akte.hideCalculation") : t("akte.showCalculation")}
              </button>
              {rechenweg && <dl className="akte__facts">{bRechenweg}</dl>}
            </>}
        {bAktionen}
      </Section>

      <Section code="verkaufspreis" i18n={i18n}>
        {stand !== null && <dl className="akte__facts">{bVerkauf}</dl>}

        {/* Freigegebene Listenpreise -- die kaufmaennischen Entscheidungen. */}
        <h4 className="akte__subtitle">{t("akte.releases")}</h4>
        {calc && calc.releases.length === 0 && <p className="akte__note">{t("akte.noRelease")}</p>}
        {bFreigaben}
        {bFreigabeForm}
        {bFehler}
      </Section>
    </>
  );
}

/**
 * Eine Kalkulationszahl in der Schreibweise der Anzeigesprache.
 *
 * DIE DATENBANKSKALA IST KEINE MESSGENAUIGKEIT.
 *
 * Die Kalkulationsspalten sind `numeric(14,6)`. Sie liefern deshalb
 * „52.010000" fuer einen Einkaufspreis von 52,01 USD und „5.000000" fuer
 * fuenf Prozent. Diese Nullen stehen fuer nichts -- sie sind die Breite der
 * Spalte, nicht die Genauigkeit des Wertes, und in einer Zahlenkolonne lesen
 * sie sich wie eine Praezision, die niemand behauptet hat.
 *
 * Abgeschnitten werden deshalb nur NACHLAUFENDE Nullen, und zwei
 * Nachkommastellen bleiben immer stehen: Betraege ohne Cent sehen sonst nach
 * gerundeten Zahlen aus. Was echte Stellen traegt -- 6,78132 USD Fracht --
 * behaelt sie vollstaendig. Gerundet wird hier nichts.
 */
/* Auch der Arbeitsbereich Einkauf zeigt jetzt Betraege (`ArticleLogistics.tsx`).
   Dieselbe Funktion, damit 94,00 USD dort nicht anders aussieht als hier. */
export function betrag(raw: string, lang: string): string {
  const gekuerzt = raw.includes(".") ? raw.replace(/0+$/, "").replace(/\.$/, "") : raw;
  const punkt = gekuerzt.indexOf(".");
  const stellen = Math.max(2, punkt === -1 ? 0 : gekuerzt.length - punkt - 1);
  const n = Number(gekuerzt);
  if (!Number.isFinite(n)) return raw;
  return n.toLocaleString(lang === "en" ? "en-GB" : "de-DE",
    { minimumFractionDigits: stellen, maximumFractionDigits: stellen });
}

/**
 * ZAHLEN NACH IHRER BEDEUTUNG, NICHT NACH IHRER GENAUIGKEIT.
 *
 * `betrag` zeigte so viele Nachkommastellen, wie die Datenbank lieferte --
 * und die liefert die volle Rechengenauigkeit: `62,766697 USD`. Das ist im
 * Rechenmodell richtig und in einer Preisansicht falsch. Niemand nennt einen
 * Einstandspreis mit sechs Nachkommastellen.
 *
 * Die Genauigkeit bleibt unangetastet -- sie steht weiterhin vollstaendig in
 * `article_calculations` und geht vollstaendig in jede Folgerechnung ein.
 * Gerundet wird ausschliesslich die ANZEIGE, und zwar nach dem, was die Zahl
 * bedeutet:
 *
 *   Geld      zwei Stellen, kaufmaennisch      52,01 USD
 *   Prozent   zwei Stellen                     2,70 %
 *   Kurs      zwei Stellen                     1,16
 *   Volumen   sechs Stellen                    0,014292 cbm
 *
 * Das Volumen ist die begruendete Ausnahme: bei Kubikmetern je Stueck steht
 * die Aussage in der sechsten Stelle.
 */
const zahlMit = (raw: string, lang: string, stellen: number): string => {
  const n = Number(raw);
  if (!Number.isFinite(n)) return raw;
  return n.toLocaleString(lang === "en" ? "en-GB" : "de-DE",
    { minimumFractionDigits: stellen, maximumFractionDigits: stellen });
};
const geld   = (raw: string, lang: string): string => zahlMit(raw, lang, 2);
/* Vorgabesaetze in der Kette (Zielmarge 55 %, Provision 5 %): ohne
   Nachkommanullen, aber ohne etwas zu verschlucken -- 52,5 bleibt 52,5. */
const satzKurz = (raw: string, lang: string): string => {
  const n = Number(raw);
  return Number.isFinite(n)
    ? n.toLocaleString(lang === "en" ? "en-GB" : "de-DE", { maximumFractionDigits: 2 }) : raw;
};

/**
 * Die Herleitung des VerpackungsG -- Menge mal Satz, je Materialart.
 *
 * „0,43 EUR" allein ist eine Zahl, die man glauben muss. „918 g Pappe zu
 * 237 EUR/t + 211 g Kunststoff zu 996 EUR/t" ist eine, die man im Vorbeigehen
 * nachrechnen kann -- und genau daran faellt auf, wenn eine Menge nicht
 * stimmt. Materialarten ohne Menge bleiben weg: eine Zeile „0 g Holz" waere
 * kein Beleg, sondern Fuellung.
 */
function herleitungVerpackung(stand: ArticleCalculation, i18n: I18n): string | undefined {
  const teile: string[] = [];
  const nimm = (gramm: string | null, satz: string | null, name: string): void => {
    if (gramm === null || satz === null || !(Number(gramm) > 0)) return;
    teile.push(i18n.t("akte.packagingActPart", {
      g: zahlMit(gramm, i18n.lang, 2), material: name,
      rate: zahlMit(String(Number(satz) * 1000), i18n.lang, 2),
    }));
  };
  nimm(stand.inputs.packagingCardboardGPerUnit, stand.inputs.packagingActCardboardEurPerKg,
       i18n.tCode("packMaterial", "cardboard"));
  nimm(stand.inputs.packagingPlasticGPerUnit, stand.inputs.packagingActPlasticEurPerKg,
       i18n.tCode("packMaterial", "plastic"));
  return teile.length === 0 ? undefined : teile.join(" + ");
}
const kurs   = (raw: string, lang: string): string => zahlMit(raw, lang, 2);
const volumen = (raw: string, lang: string): string => zahlMit(raw, lang, 6);

/**
 * Ein Prozentsatz fuer einen SATZ, nicht fuer eine Tabelle.
 */
function prozent(raw: string, lang: string): string {
  const n = Number(raw);
  if (!Number.isFinite(n)) return raw;
  return n.toLocaleString(lang === "en" ? "en-GB" : "de-DE",
    { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

/**
 * Der Arbeitsstand eines Produktes in einer Zeile.
 *
 * Kein Zaehler ohne Deckung: jede Zahl kommt aus einem vorhandenen Datensatz,
 * und ein Zustand ohne Faelle erscheint gar nicht. Drei Nullen nebeneinander
 * waeren eine Wand ohne Aussage.
 */
function ProduktStatus({ akte, a, i18n }: {
  akte: ArticleDossier | null; a: ArticleDetail; i18n: I18n;
}): React.JSX.Element | null {
  const [ungeprueft, setUngeprueft] = useState<number | null>(null);
  useEffect(() => {
    let weg = false;
    api.articleProperties(a.id)
      .then((r) => { if (!weg) setUngeprueft(
        r.items.filter((v) => v.quality === "pruefung_erforderlich").length); })
      .catch(() => undefined);
    return () => { weg = true; };
  }, [a.id]);

  /*
   * NUR DIE VORSCHLAEGE, DIE NOCH EINE ENTSCHEIDUNG VERLANGEN.
   *
   * Ein Vorschlag, dessen Wert bereits gilt, steht weiterhin offen -- aber er
   * ist keine offene ARBEIT mehr. Ihn mitzuzaehlen hiesse, jemanden zu einer
   * Entscheidung aufzufordern, die nichts mehr aendern kann -- und die die API
   * inzwischen mit 409 abweist. Er bleibt am Merkmal sichtbar, mit Quelle und
   * Wortlaut; nur die Aufgabenzahl meint ihn nicht mehr.
   *
   * Die Zahl kommt aus `proposalTally` und nicht aus einem Filter ueber die
   * gelieferte Liste: die Auswertung zaehlt nach derselben Definition, nach
   * der die API entscheidet. Ein Filter hier waere eine zweite Meinung.
   */
  const vorschlaege = akte?.proposalTally.open ?? 0;
  const hinweise = a.reviewFlags.length;
  if (vorschlaege === 0 && (ungeprueft ?? 0) === 0 && hinweise === 0) return null;
  return (
    <ul className="akte__stand" aria-label={i18n.t("akte.openWork")}>
      {vorschlaege > 0 && (
        <li className="akte__standitem akte__standitem--vorschlag">
          {i18n.t("akte.openProposals", { count: vorschlaege })}
        </li>
      )}
      {(ungeprueft ?? 0) > 0 && (
        <li className="akte__standitem">
          {i18n.t("akte.openChecks", { count: ungeprueft ?? 0 })}
        </li>
      )}
      {hinweise > 0 && (
        <li className="akte__standitem">
          {i18n.t("akte.openFlags", { count: hinweise })}
        </li>
      )}
    </ul>
  );
}

/**
 * WOHER EINE ZAHL KOMMT -- die Frage, die vor jeder Kalkulationszeile steht.
 *
 * Bis hierher sahen Eingabe, zentrale Vorgabe und Rechenergebnis gleich aus.
 * Wer den Wechselkurs aendern wollte, suchte ein Eingabefeld, das es nicht
 * gibt; wer den Listenpreis fuer eine Eingabe hielt, suchte eines, das es
 * nicht geben DARF.
 *
 *   eingabe   an diesem Artikel gepflegt      EXW, Volumen
 *   vorgabe   kommt von einer anderen Stelle  Kurs, Zollsatz, Zielmarge
 *   ergebnis  gerechnet, nirgends eingegeben  Landed Cost, Listenpreis
 */
type Herkunft = "eingabe" | "vorgabe" | "ergebnis";

/**
 * Die Kalkulation als Kette -- die Darstellung der PRODUKTAKTE.
 *
 * Jedes Glied ist ein Wert DIESES Kalkulationsstands; nichts wird hier
 * gerechnet. Die Arbeitsflaeche im Reiter Einkauf zeigt stattdessen die
 * kaufmaennische Strecke (`Strecke`).
 */
function Kette({ stand, i18n }: { stand: ArticleCalculation; i18n: I18n }): React.JSX.Element {
  const t = i18n.t;
  const u = <Unknown />;
  const mit = (v: string | null, f: (x: string) => string, einheit: string): React.ReactNode =>
    v === null ? u : <>{f(v)} {einheit}</>;
  const g = (x: string): string => geld(x, i18n.lang);
  const glieder: readonly { key: string; label: string; wert: React.ReactNode;
                            neben?: string | null; art: Herkunft }[] = [
    { key: "ek", label: t("kette.ek"), art: "eingabe",
      wert: mit(stand.inputs.exwAmount, g, stand.inputs.exwCurrency ?? "") },
    { key: "kurs", label: t("kette.rate"), art: "vorgabe",
      wert: stand.inputs.usdEurCalcRate === null ? u : kurs(stand.inputs.usdEurCalcRate, i18n.lang) },
    { key: "fracht", label: t("kette.freight"), art: "ergebnis",
      wert: mit(stand.steps.freightUsd, g, "USD"),
      neben: stand.inputs.transportMode ? i18n.tCode("transport", stand.inputs.transportMode) : null },
    { key: "zoll", label: t("kette.duty"), art: "ergebnis",
      wert: mit(stand.steps.dutyUsd, g, "USD"),
      neben: stand.inputs.dutyRatePercent === null ? null
        : `${prozent(stand.inputs.dutyRatePercent, i18n.lang)} %` },
    { key: "verpackungsg", label: t("kette.packagingAct"), art: "ergebnis",
      wert: mit(stand.steps.packagingActEur, g, "EUR") },
    { key: "elektrog", label: t("kette.electroAct"), art: "ergebnis",
      wert: mit(stand.steps.electroActEur, g, "EUR") },
    { key: "einstand", label: t("kette.costBase"), art: "ergebnis",
      wert: mit(stand.steps.costBaseEur, g, "EUR") },
    { key: "marge", label: t("kette.margin"), art: "vorgabe",
      wert: stand.inputs.minimumMarginPercent === null ? u
        : `${prozent(stand.inputs.minimumMarginPercent, i18n.lang)} %`,
      neben: stand.inputs.marginFromFamily
        ? t("akte.marginFromFamily", { family: stand.inputs.marginFromFamily }) : null },
    { key: "liste", label: t("kette.listPrice"), art: "ergebnis",
      wert: mit(stand.calculatedListPriceEur, g, "EUR") },
  ];
  return (
    <ol className="kette" aria-label={t("kette.label")}>
      {glieder.map((x) => (
        <li key={x.key} className={`kette__glied kette__glied--${x.art} kette__glied--${x.key}`}>
          <span className="kette__label">{x.label}</span>
          <span className="kette__wert">{x.wert}</span>
          {x.neben && <span className="kette__neben">{x.neben}</span>}
        </li>
      ))}
    </ol>
  );
}

/**
 * DER TRANSPORTVERGLEICH -- intern, gerechnet, nicht gespeichert.
 *
 * Drei Karten: der AKTIVE Weg (sein Preis ist der des Kalkulationsstands,
 * nur er ist freigebbar) und zwei Vergleichsszenarien, gerechnet vom Server
 * mit derselben Engine, demselben Parametersatz und denselben Eingaben --
 * getauscht wird nur die Transportart und ihr zentraler Frachtsatz. Fehlt
 * ein Satz, steht „nicht berechenbar" und was fehlt; kein Ersatzwert.
 *
 * Die Szenarien haben keinen Knopf, keine Freigabe und keinen Weg nach
 * aussen. Differenzen werden zwischen FRISCH gerechneten Werten gebildet,
 * damit sie dieselben Eingaben vergleichen; hat sich seit dem Stand etwas
 * geaendert, sagt ein Hinweis, dass neu zu rechnen ist.
 */
/*
 * DIE LUFTFRACHT-HERLEITUNG (0058) -- zweimal dieselben Zahlen: ausfuehrlich
 * in den Berechnungsdetails, knapp auf der Szenariokarte. Beide lesen aus
 * derselben Antwort des Servers; gerechnet wird hier nichts.
 */
const kg = (v: string, lang: string): string => zahlMit(v, lang, 2);
function LuftFakten({ air, i18n }: { air: AirFreightDerivation; i18n: I18n }): React.JSX.Element {
  const t = i18n.t;
  const u = <Unknown />;
  return (<>
    <Fact label={t("luft.master")} herkunft="eingabe">
      {air.masterLengthMm === null ? u
        : t("luft.masterValue", { l: zahlMit(air.masterLengthMm, i18n.lang, 0),
            b: zahlMit(air.masterWidthMm!, i18n.lang, 0), h: zahlMit(air.masterHeightMm!, i18n.lang, 0) })}
    </Fact>
    <Fact label={t("luft.gross")} herkunft="eingabe">
      {air.masterGrossWeightKg === null ? u : <>{kg(air.masterGrossWeightKg, i18n.lang)} kg</>}
    </Fact>
    <Fact label={t("luft.units")} herkunft="eingabe">
      {air.masterUnitCount === null ? u : air.masterUnitCount}
    </Fact>
    <Fact label={t("luft.divisor")} herkunft="vorgabe">
      {air.volumetricDivisor === null ? u : <>{zahlMit(air.volumetricDivisor, i18n.lang, 0)} cm³/kg</>}
    </Fact>
    <Fact label={t("luft.volumetric")} herkunft="ergebnis">
      {air.volumetricWeightKg === null ? u : <>{kg(air.volumetricWeightKg, i18n.lang)} kg</>}
    </Fact>
    <Fact label={t("luft.chargeable")} herkunft="ergebnis"
          quelle={air.basis === null ? undefined : t(air.basis === "volumen" ? "luft.basis_volumen" : "luft.basis_gewicht")}>
      {air.chargeableWeightKg === null ? u : <strong>{kg(air.chargeableWeightKg, i18n.lang)} kg</strong>}
    </Fact>
    <Fact label={t("luft.rate")} herkunft="vorgabe">
      {air.freightRateUsdPerKg === null ? t("luft.rateMissing")
        : <>{geld(air.freightRateUsdPerKg, i18n.lang)} USD/kg</>}
    </Fact>
    <Fact label={t("luft.freightMaster")} herkunft="ergebnis">
      {air.freightMasterUsd === null ? u : <>{geld(air.freightMasterUsd, i18n.lang)} USD</>}
    </Fact>
    <Fact label={t("luft.freightUnit")} herkunft="ergebnis">
      {air.freightPerUnitUsd === null ? u : <>{geld(air.freightPerUnitUsd, i18n.lang)} USD</>}
    </Fact>
  </>);
}
function LuftKurz({ air, i18n }: { air: AirFreightDerivation; i18n: I18n }): React.JSX.Element {
  const t = i18n.t;
  return (
    <span className="vergleich__luft">
      {air.masterLengthMm !== null && (
        <span>{t("luft.masterValue", { l: zahlMit(air.masterLengthMm, i18n.lang, 0),
          b: zahlMit(air.masterWidthMm!, i18n.lang, 0), h: zahlMit(air.masterHeightMm!, i18n.lang, 0) })}
          {air.masterGrossWeightKg !== null && <> · {kg(air.masterGrossWeightKg, i18n.lang)} kg</>}
          {air.masterUnitCount !== null && <> · {t("luft.unitsShort", { n: air.masterUnitCount })}</>}</span>
      )}
      {air.chargeableWeightKg !== null && (
        <span>{t("luft.chargeableShort", { kg: kg(air.chargeableWeightKg, i18n.lang),
          basis: t(air.basis === "volumen" ? "luft.basisShort_volumen" : "luft.basisShort_gewicht") })}</span>
      )}
      {air.freightPerUnitUsd !== null && (
        <span>{t("luft.freightShort", { rate: geld(air.freightRateUsdPerKg!, i18n.lang),
          master: geld(air.freightMasterUsd!, i18n.lang), unit: geld(air.freightPerUnitUsd, i18n.lang) })}</span>
      )}
    </span>
  );
}

/**
 * Der Zustand des aktuell berechneten Listenpreises -- EINE Formulierung fuer
 * das Listenpreis-Glied der Kette und fuer den Block „Listenpreis nach
 * Importweg". Ist ein anderer Preis freigegeben als der berechnete, sagt der
 * Zustand welcher.
 */
function preisZustand(calc: ArticleCalculationResponse | null, i18n: I18n): React.JSX.Element {
  const t = i18n.t;
  const heute = heuteLokal();
  const stand = calc?.calculations[0] ?? null;
  const geltend = calc?.releases.find((r) => r.validFrom <= heute && r.listPriceEur !== null) ?? null;
  const berechnet = stand?.calculatedListPriceEur ?? null;
  return geltend === null
    ? <span className="kalk__status">{t("head.priceCalculated")}</span>
    : berechnet !== null && Number(berechnet) === Number(geltend.listPriceEur)
      ? <span className="kalk__status kalk__status--frei">
          {t("head.priceReleased", { date: geltend.validFrom })}
        </span>
      : <span className="kalk__status kalk__status--frei">
          {t("kalk.releasedOther", { value: geld(geltend.listPriceEur!, i18n.lang), date: geltend.validFrom })}
        </span>;
}

/**
 * LISTENPREIS NACH IMPORTWEG -- die Entscheidung oben im Einkauf (Importweg 001).
 *
 * Im Verkaufsgespraech zaehlen zwei Dinge: was das Produkt im Einkauf kostet
 * (links, die Lieferantenkarte) und welcher Listenpreis sich je nach
 * Importweg ergibt (rechts, dieser Block). SEA fuer den, der warten kann,
 * TRAIN als Standardweg, AIR fuer den, der es schnell braucht.
 *
 * NICHTS WIRD HIER GERECHNET. Der Block liest den juengsten Kalkulationsstand
 * und die Szenarien ueber dieselben Endpunkte wie die Listenpreiskalkulation
 * darunter und zeigt sie mit derselben Komponente. Die Preise sind
 * KALKULIERT, nicht freigegeben -- der Zustand steht dabei.
 *
 * `version` erhoeht der Rahmen nach jeder Aenderung der Kalkulation darunter
 * (Neu rechnen, Transportart, EXW, Saetze), damit oben dieselben Zahlen stehen.
 */
export function ImportwegPreise({ articleId, i18n, version }: {
  articleId: number; i18n: I18n; version: number;
}): React.JSX.Element {
  const t = i18n.t;
  const [calc, setCalc] = useState<ArticleCalculationResponse | null>(null);
  const [fehler, setFehler] = useState(false);
  useEffect(() => {
    let aktuell = true;
    setFehler(false);
    api.articleCalculation(articleId)
      .then((c) => { if (aktuell) setCalc(c); })
      .catch(() => { if (aktuell) setFehler(true); });
    return () => { aktuell = false; };
  }, [articleId, version]);
  const stand = calc?.calculations[0] ?? null;
  if (stand === null) {
    return (
      <section className="vergleich vergleich--kompakt" aria-label={t("importweg.title")}>
        <header className="importweg__kopf">
          <h4 className="importweg__titel">{t("importweg.title")}</h4>
        </header>
        <p className="kalk__status">
          {fehler ? t("vergleich.error") : calc === null ? "…" : t("importweg.none")}
        </p>
      </section>
    );
  }
  return <Transportvergleich articleId={articleId} stand={stand} i18n={i18n} kompakt
                             status={preisZustand(calc, i18n)} />;
}

function Transportvergleich({ articleId, stand, i18n, kompakt = false, status }: {
  articleId: number; stand: ArticleCalculation; i18n: I18n;
  /**
   * Die Entscheidungsansicht oben im Einkauf (Importweg 001): je Weg Icon,
   * Name, kalkulierter Listenpreis, aktiver Weg markiert -- ohne Differenz,
   * ohne Herleitung. Dieselben Daten, dieselbe Szenariorechnung.
   */
  kompakt?: boolean;
  /** Der Zustand des Preises (berechnet · nicht freigegeben / freigegeben). */
  status?: React.ReactNode;
}): React.JSX.Element {
  const t = i18n.t;
  const [daten, setDaten] = useState<CalculationScenariosResponse | null>(null);
  const [fehler, setFehler] = useState(false);
  useEffect(() => {
    let aktuell = true;
    setDaten(null); setFehler(false);
    api.calculationScenarios(articleId)
      .then((d) => { if (aktuell) setDaten(d); })
      .catch(() => { if (aktuell) setFehler(true); });
    return () => { aktuell = false; };
  }, [articleId, stand.id]);
  const aktiv = stand.inputs.transportMode ?? "train";
  const frischAktiv = daten?.scenarios.find((x) => x.transportMode === aktiv)?.listPriceEur ?? null;
  const veraltet = frischAktiv !== null && stand.calculatedListPriceEur !== null
    && Number(frischAktiv) !== Number(stand.calculatedListPriceEur);
  const ICON: Record<string, () => React.JSX.Element> = { sea: IconSchiff, train: IconZug, air: IconFlugzeug };
  /* Transportlaufzeit und Nutzen je Weg -- aus der zentralen Konfiguration (lib/transportwege.ts). */
  const laufzeit = (art: "sea" | "train" | "air"): string => {
    const l = TRANSPORTLAUFZEIT[art];
    return l.einheit === "tage" ? t("importweg.laufzeitTage", { n: l.wert })
      : l.wert === 1 ? t("importweg.laufzeitWoche") : t("importweg.laufzeitWochen", { n: l.wert });
  };
  const NUTZEN = { sea: t("importweg.nutzen_sea"), train: t("importweg.nutzen_train"), air: t("importweg.nutzen_air") };
  /*
   * DIE TATSAECHLICHE URSACHE -- jede einzeln. Bei Luftfracht koennen es
   * mehrere sein (Satz UND Bruttogewicht); eine Sammelmeldung „Angaben
   * fehlen" liesse raten, was zu pflegen ist.
   */
  const LUFT_GRUND: Record<string, string> = {
    air_freight_usd_per_kg: t("vergleich.airRateMissing"),
    master_dimensions: t("vergleich.masterDimensionsMissing"),
    master_gross_weight: t("vergleich.masterGrossMissing"),
    master_unit_count: t("vergleich.masterUnitsMissing"),
    air_volumetric_divisor_cm3_per_kg: t("vergleich.divisorMissing"),
  };
  const grund = (art: string, fehlt: readonly string[]): string => {
    if (art === "air") {
      const g = fehlt.map((f) => LUFT_GRUND[f]).filter((x): x is string => x !== undefined);
      if (g.length > 0) return g.join(" · ");
    }
    return fehlt.some((f) => /freight/.test(f)) ? t("vergleich.rateMissing") : t("vergleich.inputsMissing");
  };
  return (
    <section className={kompakt ? "vergleich vergleich--kompakt" : "vergleich"}
             aria-label={kompakt ? t("importweg.title") : t("vergleich.title")}>
      {kompakt ? (
        <header className="importweg__kopf">
          <h4 className="importweg__titel">{t("importweg.title")}</h4>
          {status}
        </header>
      ) : (<>
        <h5 className="kalk__detailtitel">{t("vergleich.title")}</h5>
        <p className="vergleich__hinweis">{t("vergleich.note")}</p>
      </>)}
      {fehler && <p className="kalk__status">{t("vergleich.error")}</p>}
      <ul className="vergleich__karten">
        {(["sea", "train", "air"] as const).map((art) => {
          const Icon = ICON[art]!;
          const sz = daten?.scenarios.find((x) => x.transportMode === art) ?? null;
          const istAktiv = art === aktiv;
          const preis = istAktiv ? stand.calculatedListPriceEur : sz?.listPriceEur ?? null;
          const diff = !istAktiv && sz?.listPriceEur != null && frischAktiv !== null
            ? Math.round((Number(sz.listPriceEur) - Number(frischAktiv)) * 100) / 100 : null;
          return (
            <li key={art} className={istAktiv ? "vergleich__karte vergleich__karte--aktiv" : "vergleich__karte"}
                data-art={art}>
              <span className="vergleich__kopf">
                <Icon />
                <span className="vergleich__art">{i18n.tCode("transport", art)}</span>
                {istAktiv && <span className="vergleich__aktiv">{t("vergleich.active")}</span>}
              </span>
              {daten === null && !istAktiv && !fehler ? <span className="kalk__status">…</span>
                : preis === null ? (
                  <span className="vergleich__nicht">
                    {t("vergleich.notComputable")}
                    <span className="vergleich__grund">{grund(art, istAktiv ? stand.missingInputs : sz?.missingInputs ?? [])}</span>
                  </span>
                ) : (
                  <span className="vergleich__preis">{geld(preis, i18n.lang)} EUR</span>
                )}
              {!kompakt && diff !== null && (
                <span className={diff < 0 ? "vergleich__diff vergleich__diff--minus" : "vergleich__diff"}>
                  {t("vergleich.diff", { value: `${diff > 0 ? "+" : diff < 0 ? "−" : "±"}${geld(String(Math.abs(diff)), i18n.lang)}` })}
                </span>
              )}
              <span className="vergleich__laufzeit">
                <span className="vergleich__laufzeitlabel">{t("importweg.laufzeit")}</span>
                <span className="vergleich__laufzeitwert">{laufzeit(art)}</span>
              </span>
              <span className="vergleich__weg">{NUTZEN[art]}</span>
              {/* Luft: woraus sich die Fracht ergibt -- auch ohne Satz, denn
                  das abrechenbare Gewicht ist schon belegt. */}
              {!kompakt && art === "air" && (istAktiv ? stand.inputs.air : sz?.air ?? null) !== null && (
                <LuftKurz air={(istAktiv ? stand.inputs.air : sz!.air)!} i18n={i18n} />
              )}
            </li>
          );
        })}
      </ul>
      {!kompakt && veraltet && <p className="kalk__status">{t("vergleich.stale")}</p>}
      {/* Ruhig und untergeordnet: kalkuliert, nicht freigegeben, nicht verbindlich. */}
      {kompakt && <p className="importweg__hinweis">{t("importweg.hinweis")}</p>}
      {daten !== null && daten.currentParameterSetId !== null && daten.currentParameterSetId !== daten.parameterSetId && (
        <p className="kalk__status vergleich__neuersatz">{t("vergleich.newerSet")}</p>
      )}
    </section>
  );
}

/**
 * DIE KAUFMAENNISCHE STRECKE -- die Arbeitsflaeche im Reiter Einkauf.
 *
 * Elf Boxen in der Lesart, in der greenius kalkuliert:
 *   EXW USD -> Fracht -> CSEF -> Zoll -> HH Landed -> VerpackungsG ->
 *   ElektroG -> Zielmarge -> Provision -> Rabattbasis -> Listenpreis
 *
 * VIEW FIRST. Keine Box traegt ein offenes Feld. Einen Stift tragen die Werte,
 * die in DIESEM Kontext entschieden werden -- EXW und Transportart -- und
 * die drei Saetze an ihrer Quelle (UX-Nachtrag 002, `satzBearbeiten`):
 *   EXW USD   der Einkaufspreis (price.input.write) -- als neue Preisstufe,
 *             ueber denselben Weg wie „Einkaufspreis erfassen"
 *   Fracht    die TRANSPORTART (Sea/Train/Air) -- nicht der Frachtsatz. Der
 *             Satz der gewaehlten Art kommt aus dem geltenden Parametersatz
 *             und wird mit dem Verpackungsvolumen multipliziert.
 * Beides fuehrt zu einem neuen Kalkulationsstand (derselbe Weg wie „Neu
 * rechnen"). CSEF und Zoll sind Saetze anderer Stellen, alles uebrige sind
 * Ergebnisse -- ohne Stift, ohne Override.
 *
 * DIE REIHENFOLGE DER ANZEIGE IST DIE DER RECHNUNG. Gerechnet wird
 * Kostenbasis / (1 - Zielmarge) / (1 - Provision) / Divisor, kaufmaennisch
 * gerundet -- und, wo das die Zielmarge unterschritte, auf den naechsten
 * Schritt erhoeht (packages/shared preisstufen). Zielmarge und Provision
 * tragen hier ihren SATZ; die Zwischenbetraege stehen in den Details.
 */
/** Die drei Saetze, die aus der Kette heraus an ihrer Quelle bearbeitet werden. */
type SatzCode = "minimum_margin_percent" | "commission_percent" | "list_price_divisor";
interface SatzBearbeitung {
  /** Familiencode, wenn der Satz als Familienabweichung gepflegt wird; sonst null (zentral). */
  quelle: (code: SatzCode) => string | null;
  speichern: (code: SatzCode, wert: number, ab: string) => Promise<void>;
  /**
   * Die juengste Revision, die es fuer dieses Gueltig-ab schon gibt -- oder
   * null. Seit 0059 kein Hindernis mehr, sondern eine Auskunft: gespeichert
   * wird die naechste Revision.
   */
  revisionAm: (ab: string) => number | null;
}

function Strecke({ stand, i18n, listenStatus, exwBearbeiten, frachtAendern, satzBearbeiten }: {
  stand: ArticleCalculation; i18n: I18n;
  listenStatus: React.ReactNode;
  /** Vorhanden, wenn der EK hier erfasst werden darf (Recht und genau eine Beziehung). */
  exwBearbeiten?: ((betrag: string, waehrung: string, ab: string) => Promise<void>) | undefined;
  /** Vorhanden, wenn hier neu gerechnet werden darf. */
  frachtAendern?: ((art: string) => Promise<void>) | undefined;
  /**
   * Vorhanden mit `price.parameter.write`: Zielmarge, Provision und
   * Rabattbasis an ihrer QUELLE aendern. `quelle` sagt, wo der Satz heute
   * gepflegt wird -- Familie oder zentral --, damit der Editor es sagen kann.
   */
  satzBearbeiten?: SatzBearbeitung | undefined;
}): React.JSX.Element {
  const t = i18n.t;
  const u = <Unknown />;
  const g = (x: string): string => geld(x, i18n.lang);
  const mit = (v: string | null, einheit: string): React.ReactNode =>
    v === null ? u : <>{g(v)} {einheit}</>;
  const [offen, setOffen] = useState<"exw" | "fracht" | SatzCode | null>(null);
  const [satzE, setSatzE] = useState("");
  const [busy, setBusy] = useState(false);
  const [art, setArt] = useState(stand.inputs.transportMode ?? "train");
  const [betragE, setBetragE] = useState("");
  const [waehrungE, setWaehrungE] = useState(stand.inputs.exwCurrency ?? "USD");
  const [abE, setAbE] = useState(heuteLokal());
  /*
   * KEIN STILLES SCHEITERN (Browser-Befunde 001, B7).
   *
   * Scheitert das Speichern, bleibt der Editor offen und sagt IN SICH, was
   * nicht ging. Bisher stand der Fehler nur unter der Freigabe -- ausserhalb
   * des Blicks --, und der Knopf wirkte funktionslos.
   */
  const [editorFehler, setEditorFehler] = useState<string | null>(null);
  const lauf = async (f: () => Promise<void>): Promise<void> => {
    setBusy(true); setEditorFehler(null);
    try { await f(); setOffen(null); }
    catch (e) { setEditorFehler(errorText(i18n, e)); }
    finally { setBusy(false); }
  };
  const divisor = stand.inputs.listPriceDivisor === null ? null : Number(stand.inputs.listPriceDivisor);
  const rabattbasis = divisor === null || divisor <= 0 || divisor > 1 ? null
    : ((1 - divisor) * 100).toLocaleString(i18n.lang === "en" ? "en-GB" : "de-DE", { maximumFractionDigits: 2 });

  const stift = (was: "exw" | "fracht" | SatzCode, label: string): React.ReactNode => (
    <button type="button" className="strecke__stift" aria-label={t("strecke.edit", { what: label })}
      title={t("strecke.edit", { what: label })} disabled={busy}
      onClick={() => { setSatzE(""); setEditorFehler(null); setOffen(offen === was ? null : was); }}>✎</button>
  );
  /*
   * DER SATZ-EDITOR -- Zielmarge, Provision, Rabattbasis (UX-Nachtrag 002).
   *
   * Er aendert die Stelle, an der der Satz WIRKLICH steht, und sagt vorher,
   * was das bedeutet: eine Familienabweichung gilt fuer die ganze Familie,
   * ein zentraler Satz fuer alle Artikel ohne Abweichung -- als neuer
   * Parametersatz ab dem gewaehlten Tag. Einen Artikelwert gibt es nicht und
   * entsteht hier nicht. Die Rabattbasis wird in Prozent eingegeben und als
   * Divisor gespeichert (50 % -> 0,50), so wie gerechnet wird.
   */
  const satzEditor = (code: SatzCode, label: string, jetzt: string | null): React.ReactNode => {
    if (!satzBearbeiten || offen !== code) return undefined;
    const familie = satzBearbeiten.quelle(code);
    const zahlE = Number(satzE.replace(",", "."));
    const gueltig = satzE.trim() !== "" && Number.isFinite(zahlE) && zahlE >= 0 && zahlE < 100
      && (code !== "list_price_divisor" || zahlE > 0);
    const gespeichert = code === "list_price_divisor" ? 1 - zahlE / 100 : zahlE;
    const d = code === "list_price_divisor" && gueltig ? 1 - zahlE / 100 : null;
    /* Zentral und fuer dieses Datum gibt es schon Revisionen: sagen, welche entsteht. */
    const letzteRevision = familie === null && abE !== "" ? satzBearbeiten.revisionAm(abE) : null;
    return (
      <div className="strecke__editor strecke__editor--satz">
        <span className="strecke__satzeingabe">
          <input className="input" inputMode="decimal" aria-label={label} value={satzE}
            placeholder={jetzt ?? ""} onChange={(e) => setSatzE(e.target.value)} />
          <span>%</span>
        </span>
        {d !== null && (
          <span className="strecke__hinweis">
            {t("satz.divisorFrom", { divisor: kurs(String(d), i18n.lang),
              faktor: zahlMit(String(1 / d), i18n.lang, 2) })}
          </span>
        )}
        {familie === null && (
          <input className="input" type="date" aria-label={t("akte.validFrom")} value={abE}
            onChange={(e) => setAbE(e.target.value)} />
        )}
        <span className="strecke__geltung">
          {familie !== null
            ? t("satz.scopeFamily", { family: familie })
            : t("satz.scopeCentral", { date: abE })}
        </span>
        <span className="strecke__editoraktionen">
          <button className="btn btn--primary btn--small"
            disabled={busy || !gueltig || (familie === null && abE === "")}
            onClick={() => void lauf(() => satzBearbeiten.speichern(code, gespeichert, abE))}>
            {t("strecke.recalc")}</button>
          <button className="btn btn--small" onClick={() => setOffen(null)}>{t("common.cancel")}</button>
        </span>
        {letzteRevision !== null && (
          <span className="strecke__hinweis" role="status">
            {t("satz.nextRevision", { date: abE, revision: letzteRevision, next: letzteRevision + 1 })}
          </span>
        )}
        {editorFehler !== null && <span className="strecke__fehler" role="alert">{editorFehler}</span>}
      </div>
    );
  };

  const boxen: readonly { key: string; label: string; wert: React.ReactNode; neben?: React.ReactNode;
                          art: Herkunft; stift?: React.ReactNode; editor?: React.ReactNode }[] = [
    { key: "exw", label: t("strecke.exw"), art: "eingabe",
      wert: mit(stand.inputs.exwAmount, stand.inputs.exwCurrency ?? ""),
      stift: exwBearbeiten ? stift("exw", t("strecke.exw")) : undefined,
      editor: offen === "exw" && exwBearbeiten ? (
        <div className="strecke__editor">
          <input className="input" inputMode="decimal" aria-label={t("akte.amount")} value={betragE}
            placeholder={stand.inputs.exwAmount ?? ""} onChange={(e) => setBetragE(e.target.value)} />
          <select className="input" aria-label={t("akte.currency")} value={waehrungE}
            onChange={(e) => setWaehrungE(e.target.value)}>
            {PRICE_CURRENCIES.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          <input className="input" type="date" aria-label={t("akte.validFrom")} value={abE}
            onChange={(e) => setAbE(e.target.value)} />
          <span className="strecke__editoraktionen">
            <button className="btn btn--primary btn--small" disabled={busy || betragE === "" || abE === ""}
              onClick={() => void lauf(() => exwBearbeiten(betragE, waehrungE, abE))}>{t("common.save")}</button>
            <button className="btn btn--small" onClick={() => setOffen(null)}>{t("common.cancel")}</button>
          </span>
          <span className="strecke__hinweis">{t("strecke.exwHint")}</span>
          {editorFehler !== null && <span className="strecke__fehler" role="alert">{editorFehler}</span>}
        </div>
      ) : undefined },
    { key: "fracht", label: t("kette.freight"), art: "ergebnis",
      neben: stand.inputs.transportMode ? i18n.tCode("transport", stand.inputs.transportMode) : null,
      wert: mit(stand.steps.freightUsd, "USD"),
      stift: frachtAendern ? stift("fracht", t("kette.freight")) : undefined,
      editor: offen === "fracht" && frachtAendern ? (
        <div className="strecke__editor">
          <span className="strecke__arten" role="radiogroup" aria-label={t("akte.transportMode")}>
            {(["sea", "train", "air"] as const).map((a) => (
              <label key={a} className="strecke__art">
                <input type="radio" name="strecke-art" value={a} checked={art === a}
                  onChange={() => setArt(a)} />
                {i18n.tCode("transport", a)}
              </label>
            ))}
          </span>
          <span className="strecke__editoraktionen">
            <button className="btn btn--primary btn--small"
              disabled={busy || art === (stand.inputs.transportMode ?? "train")}
              onClick={() => void lauf(() => frachtAendern(art))}>{t("strecke.recalc")}</button>
            <button className="btn btn--small" onClick={() => { setArt(stand.inputs.transportMode ?? "train"); setOffen(null); }}>
              {t("common.cancel")}</button>
          </span>
          <span className="strecke__hinweis">{t("strecke.freightHint")}</span>
          {editorFehler !== null && <span className="strecke__fehler" role="alert">{editorFehler}</span>}
        </div>
      ) : undefined },
    { key: "csef", label: stand.inputs.csefPercent === null ? "CSEF"
        : `CSEF ${prozent(stand.inputs.csefPercent, i18n.lang)} %`, art: "ergebnis",
      wert: mit(stand.steps.csefUsd, "USD") },
    { key: "zoll", label: t("kette.duty"), art: "ergebnis",
      neben: stand.inputs.dutyRatePercent === null ? null : `${prozent(stand.inputs.dutyRatePercent, i18n.lang)} %`,
      wert: mit(stand.steps.dutyUsd, "USD") },
    { key: "landed", label: t("strecke.landed"), art: "ergebnis",
      wert: mit(stand.steps.landedCostEur, "EUR"),
      neben: stand.steps.landedCostUsd === null ? null : `${g(stand.steps.landedCostUsd)} USD` },
    { key: "verpackungsg", label: t("kette.packagingAct"), art: "ergebnis",
      wert: mit(stand.steps.packagingActEur, "EUR") },
    { key: "elektrog", label: t("kette.electroAct"), art: "ergebnis",
      wert: mit(stand.steps.electroActEur, "EUR") },
    /*
     * ERST DIE MINDESTMARGE, DANN DIE PROVISION ON TOP.
     *
     * Die Provision eines externen Vermittlers wird auf den Nettoverkaufspreis
     * gezahlt und darf die Zielmarge nicht schmaelern. Deshalb steht sie
     * NACH der Marge: aus der Kostenbasis wird der Nettoerloes mit
     * Zielmarge, und darauf wird die Provision so aufgeschlagen, dass nach
     * ihrem Abzug genau dieser Erloes bleibt (packages/shared preisstufen).
     */
    { key: "marge", label: t("strecke.minMargin"), art: "vorgabe",
      wert: stand.inputs.minimumMarginPercent === null ? u : `${satzKurz(stand.inputs.minimumMarginPercent, i18n.lang)} %`,
      neben: stand.inputs.marginFromFamily,
      stift: satzBearbeiten ? stift("minimum_margin_percent", t("strecke.minMargin")) : undefined,
      editor: satzEditor("minimum_margin_percent", t("strecke.minMargin"),
        stand.inputs.minimumMarginPercent === null ? null : satzKurz(stand.inputs.minimumMarginPercent, i18n.lang)) },
    { key: "provision", label: t("strecke.commission"), art: "vorgabe",
      wert: stand.inputs.commissionPercent === null ? u : `${satzKurz(stand.inputs.commissionPercent, i18n.lang)} %`,
      neben: satzBearbeiten?.quelle("commission_percent") ?? null,
      stift: satzBearbeiten ? stift("commission_percent", t("strecke.commission")) : undefined,
      editor: satzEditor("commission_percent", t("strecke.commission"),
        stand.inputs.commissionPercent === null ? null : satzKurz(stand.inputs.commissionPercent, i18n.lang)) },
    /*
     * RABATTBASIS statt „Listenpreis-Faktor" -- dieselbe Zahl, fachlich
     * benannt: der im Listenpreis vorgesehene Vertriebsspielraum. Divisor
     * 0,50 heisst Faktor × 2 heisst: 50 % Rabatt auf den Listenpreis fuehren
     * zurueck auf den kalkulatorischen Verkaufspreis. Die Rechnung bleibt
     * die Division durch den Divisor.
     */
    { key: "faktor", label: t("strecke.discountBasis"), art: "vorgabe",
      wert: rabattbasis === null ? u : `${rabattbasis} %`,
      neben: satzBearbeiten?.quelle("list_price_divisor") ?? null,
      stift: satzBearbeiten ? stift("list_price_divisor", t("strecke.discountBasis")) : undefined,
      editor: satzEditor("list_price_divisor", t("strecke.discountBasis"), rabattbasis) },
    { key: "liste", label: t("kette.listPrice"), art: "ergebnis",
      wert: mit(stand.calculatedListPriceEur, "EUR") },
  ];
  return (
    <ol className="strecke" aria-label={t("kette.label")}>
      {boxen.map((x) => (
        <li key={x.key} className={`strecke__box strecke__box--${x.art} strecke__box--${x.key}`}>
          <span className="strecke__kopf">
            <span className="strecke__label">{x.label}</span>
            {x.stift}
          </span>
          {x.neben && <span className="strecke__neben">{x.neben}</span>}
          <span className="strecke__wert">{x.wert}</span>
          {x.key === "liste" && <span className="kette__status">{listenStatus}</span>}
          {x.editor}
        </li>
      ))}
    </ol>
  );
}

function Fact({ label, children, breit = false, herkunft, quelle }: {
  label: string; children: React.ReactNode;
  /** Nimmt die volle Breite ein -- fuer lange Texte statt kurzer Angaben. */
  breit?: boolean;
  herkunft?: Herkunft;
  /** Woher genau -- etwa „Parametersatz 12.01.2026" oder „Zolltarif 94054239". */
  quelle?: string;
}): React.JSX.Element {
  const i18n = useI18n();
  return (
    <div className={breit ? "akte__fact akte__fact--breit" : "akte__fact"}>
      <dt>
        {label}
        {herkunft && (
          <span className={`akte__herkunft akte__herkunft--${herkunft}`} title={quelle}>
            {herkunft === "eingabe" ? i18n.t("akte.herkunft_eingabe")
              : herkunft === "vorgabe" ? i18n.t("akte.herkunft_vorgabe")
              : i18n.t("akte.herkunft_ergebnis")}
          </span>
        )}
      </dt>
      <dd>
        {children}
        {quelle && <span className="akte__quelle">{quelle}</span>}
      </dd>
    </div>
  );
}
